import INTAKE_REVIEW_STRINGS from './intakeReviewStrings';

const translations = {
  en: {
    hero: {
      headline: 'She talks to Lena every morning.',
      subline:
        'Amigo Mio gives your loved one a companion who truly listens — and never forgets.',
      cta: 'Create a profile',
    },
    // #741 — second landing-page wedge for elderly users who want
    // to set up Amigo Mio for themselves. Same product, different
    // angle. Links to /signup with the self-perspective choice
    // pre-selected.
    selfWedge: {
      headline: 'Setting it up for yourself?',
      subline:
        'Yes — that works too. Create your own account; the questions just change tone to match.',
      cta: 'Create my own account',
    },
    values: [
      {
        icon: '🎙️',
        heading: 'Talks about anything',
        description:
          'Football, memories, the news, a difficult day. Amigo Mio never judges and never changes the subject.',
      },
      {
        icon: '🧠',
        heading: 'Remembers what matters',
        description:
          'Every name, every story, every preference. Your loved one never has to repeat themselves.',
      },
      {
        icon: '👨‍👩‍👧',
        heading: 'Keeps family close',
        description:
          "You'll see how they're doing — without ever intruding on their private moments.",
      },
    ],
    nonprofit: {
      badge: 'Non-profit',
      text: 'Amigo Mio is a non-profit project. We keep costs as low as possible — and your first month is completely free.',
      trial: '1 month free trial · No credit card needed',
    },
    nav: {
      signIn: 'Sign in',
      createProfile: 'Create a profile',
    },
    footer: {
      alreadyHaveAccount: 'Already have an account?',
      signIn: 'Sign in',
    },
    relations: {
      aunt: 'Aunt',
      boyfriend: 'Boyfriend',
      brother: 'Brother',
      caregiver: 'Caregiver',
      daughter: 'Daughter',
      ex: 'Ex',
      father: 'Father',
      girlfriend: 'Girlfriend',
      grandchild: 'Grandchild',
      mother: 'Mother',
      nephew: 'Nephew',
      niece: 'Niece',
      parent: 'Parent',
      partner: 'Partner',
      sibling: 'Sibling',
      sister: 'Sister',
      son: 'Son',
      spouse: 'Spouse',
      uncle: 'Uncle',
      'family member': 'Other family member',
    },
    signup: {
      title: 'Create a profile',
      relationLabel: "I am the elderly person's…",
      relationPlaceholder: '— select —',
      change: '← Change',
      terms: { agree: 'I agree to the', link: 'AmigoMio Terms', read: '(read)', hide: '(hide)', required: 'Please accept the AmigoMio Terms to create an account.' },
      name: 'Full name',
      email: 'Email address',
      password: 'Password',
      confirmPassword: 'Confirm password',
      submit: 'Create account',
      haveAccount: 'Already have an account?',
      signIn: 'Sign in',
      // #741 — first-screen choice copy.
      choice: {
        title: 'Who is this for?',
        subtitle: 'Pick the one that fits. You can change your mind on the next screen.',
        forLoved: '👵  For a loved one',
        forSelf: '🧓  For myself',
      },
      errors: {
        passwordMismatch: 'Passwords do not match.',
        generic: 'Something went wrong. Please try again.',
        emailExists: 'An account with this email already exists.',
      },
    },
    login: {
      title: 'Sign in',
      email: 'Email address',
      password: 'Password',
      submit: 'Sign in',
      noAccount: "Don't have an account?",
      signUp: 'Sign up',
      errors: {
        invalid: 'Invalid email or password.',
        generic: 'Something went wrong. Please try again.',
      },
    },
    intake: {
      title: 'Meet Mia',
      subtitle: "I'll help you set up a profile for your loved one. It takes about 5 minutes — you can talk or type.",
      talkWithMia: 'Talk with Mia',
      chatWithMia: 'Chat with Mia',
      question: 'Question',
      of: 'of',
      switchToVoice: 'Switch to voice',
      switchToText: 'Switch to text',
      inputPlaceholder: 'Type your answer…',
      tapToSpeak: 'Tap to speak',
      tapToSend: 'Tap to send',
      voiceConnecting: 'Connecting to Mia…',
      voiceReady: 'Just speak — I’m listening.',
      voiceListening: 'Mia is speaking…',
      voiceRecording: 'Listening…',
      voiceProcessing: 'Processing…',
      voiceDone: 'All done! Taking you to your dashboard…',
      voiceError: 'Connection error. Please refresh.',
      voiceMicError: 'Microphone access denied.',
      errorStart: 'Something went wrong. Please refresh and try again.',
      errorGeneric: 'Something went wrong. Please try again.',
      callPrompt: 'When you’re ready, press Start.',
      connecting: 'Connecting…',
      miaSpeaking: 'Mia is speaking…',
      connectError: 'Could not connect. Please refresh and try again.',
      startBtn: 'Start',
      startCall: 'Start',
      endCall: 'End call',
      startOver: 'Start over',
    },
    portal: {
      welcome: 'Welcome',
      logout: 'Sign out',
      startIntake: 'Start intake with Mia',
      sections: {
        profile: { title: 'Profile', description: "View and edit your loved one's profile" },
        interests: { title: 'Interests & Topics', description: 'Manage interests and blocked topics' },
        family: { title: 'Family Members', description: 'Add and manage family connections' },
        friends: { title: 'Friends', description: 'View and manage friendships' },
        health: { title: 'Health Notes', description: 'Private health and memory notes' },
        photos: { title: 'Photos', description: 'Upload family photos' },
        alerts: { title: 'Alerts', description: 'Safeguarding alerts for your loved one' },
        social: {
          title: 'Social Media', description: 'Facebook, YouTube, RSS feeds and family updates',
          family_updates: 'Family updates',
          family_updates_desc: 'Share something that happened in the family — Lena will mention it in conversation.',
          person_name_placeholder: 'Who is this about? (e.g. Carla)',
          relationship_placeholder: 'Their relationship (e.g. daughter, grandson)',
          what_happened_placeholder: 'What happened? Write an update...',
          share_update: 'Share update',
          youtube_channels: 'YouTube channels',
          youtube_desc: 'Add a YouTube channel — Lena can mention new videos.',
          channel_name_placeholder: "Channel name (e.g. Carla's cooking channel)",
          channel_url_placeholder: 'YouTube URL or handle (e.g. @carla)',
          add_channel: 'Add channel',
          rss_advanced: 'Advanced: RSS feed',
        },
        news: { title: 'News Briefing', description: 'Interest-aware news for conversations' },
        reminders: { title: 'Reminders', description: 'Set reminders and birthdays' },
        calls: { title: 'Proactive Calls', description: 'Schedule companion check-in calls' },
        companion: { title: 'Companion Settings', description: 'Configure how the companion looks, sounds, and behaves' },
        personality: {
          title: 'Personality',
          nurturer: 'Nurturer', straightTalker: 'Straight-Talker', joker: 'Joker', intellectual: 'Intellectual', listener: 'Listener',
          warmth: 'Warmth', humour: 'Humour', pace: 'Pace', formality: 'Formality', proactivity: 'Proactivity',
          warmthLeft: 'Warm & nurturing', warmthRight: 'Direct',
          humourLeft: 'Playful & funny', humourRight: 'Calm & serious',
          paceLeft: 'Chatty & expansive', paceRight: 'Concise',
          formalityLeft: 'Casual & friendly', formalityRight: 'Formal & respectful',
          proactivityLeft: 'Takes initiative', proactivityRight: "Follows the user's lead",
          preview: 'With these settings, {name} will be:',
          custom: 'Custom',
        },
        activity: { title: 'Conversation Activity', description: 'Recent conversation summaries' },
        wellbeing: {
          title: 'Wellbeing',
          description: 'Emotional wellbeing summary from conversations',
          intro: 'This is how {name} has been feeling during conversations with {companion}',
          period: 'Period',
          mood: 'Mood',
          lastCall: 'Last call',
          thisWeek: 'This week',
          lastWeek: 'Last week',
          lastMonth: 'Last month',
          noCalls: 'No calls',
          noData: 'No conversation data available yet.',
          moods: { happy: 'Happy', neutral: 'Neutral', low: 'Low' },
          periodMoods: { happy: 'Mostly positive', neutral: 'Mixed', low: 'Mostly low' },
          privacyNote: 'Based on conversation tone only \u2014 no details are shared',
        },
        gifts: {
          title: 'Gift Ideas',
          description: 'Personalised gift suggestions',
          refresh: 'Refresh suggestions',
          empty: 'Add interests to get gift ideas',
        },
        pets: { title: 'Pets', description: 'Pets and the stories around them' },
        faith: { title: 'Faith', description: 'Faith, traditions, and spiritual practice' },
        childhood: { title: 'Childhood', description: 'Stories and memories from early life' },
        work: { title: 'Working life', description: 'Career and the work they were proud of' },
        account: { title: 'Account', description: 'Your family account settings' },
      },
      tablet: {
        close_photos: 'Close',
      },
      comingSoon: 'Coming soon',
      detail: {
        backToDashboard: 'Back to dashboard',
        loading: 'Loading…',
        noIntakeTitle: 'No intake completed yet',
        noIntakeText: 'Complete the intake with Mia to fill in this section.',
        fetchError: 'Could not load data. Please try again.',
        name: 'Name',
        nickname: 'Nickname',
        age: 'Age',
        language: 'Language',
        location: 'Location',
        noInterests: 'No interests recorded yet.',
        noFamily: 'No family members recorded yet.',
        noFriends: 'No friends added yet.',
        noHealth: 'No health notes recorded.',
        familyOnly: 'Visible to family only',
        addReminder: 'Add a reminder',
        reminderPlaceholder: 'e.g. Call Sophie, take pills...',
        birthdayName: 'Name',
        birthdayDate: 'Date (MM-DD)',
        addBirthday: 'Add birthday',
        pending: 'Pending',
        completed: 'Completed',
        noReminders: 'No reminders yet.',
        dueDate: 'Due date',
        optional: 'optional',
      },
    },

    // #441 Phase 1 — tablet UI strings.
    // English is the source-of-truth namespace; other languages stub
    // these in English for now and Phase 3 fills them with real
    // translations. Phase 1 keeps scope to CallStrip status labels +
    // a couple of YouSaidCard placeholders as a proof-of-pattern; the
    // other components (PhotoCarousel, DominoGame, settings overlay,
    // etc.) get keyed in Phase 2.
    tablet: {
      callstrip: {
        // STATUS_LABEL replacements — see CallStrip.STATUS_LABEL
        // for the corresponding raw constant; keys mirror the
        // status strings so the lookup is t.tablet.callstrip.status[<status>].
        status: {
          idle: '',
          wake_listening: 'Waking up',
          listening: 'Listening',
          // transcribing + thinking collapse to one user-facing label
          // (see #407)
          transcribing: 'Let me think',
          thinking: 'Let me think',
          speaking: 'Speaking',
          reconnecting: 'Reconnecting',
          error: 'Something went wrong',
          quota: 'Resting for now',
          music_playing: 'Music is playing',
          // #687 — wait mode label. Lena is silent but the call is
          // open and the mic is still listening for the wake-name.
          waiting: 'Waiting',
        },
        endCall: 'End call',
        hiccup: 'Hiccup',
        tryingAgain: 'Trying again',
        mutedMicLabel: 'microphone muted',  // aria-label
        // #551 — strip-side YouSaidStripCard label was hardcoded
        // English; now keyed. Same word as yousaid.frozenLabel but
        // owned by the strip-card so they can diverge if the design
        // wants the strip card shorter on tight viewports.
        youSaidLabel: 'You said',
        // #551 — title (tooltip) on the connection-hiccup pill.
        // Visible to family/admin watching the screen, not the
        // elderly user, but still user-facing copy.
        hiccupTooltip: "Connection hiccup — the last turn didn't reach the server cleanly.",
        // #700 — pause/resume button labels. Used as both the
        // aria-label (screen reader) and the title (tooltip).
        // Phrasing is the spoken-equivalent of the voice sleep
        // trigger ("be quiet" / "resume") so the button feels
        // like the same action the user would say aloud.
        pauseLabel: 'Be quiet',
        resumeLabel: 'Resume',
      },
      yousaid: {
        // YouSaidCard placeholders + labels (legacy, kept for the
        // CallStrip's strip-side YouSaidStripCard and any future
        // re-introduction of the focal-area YouSaidCard).
        placeholderTitle: 'Just say something',
        placeholderBody: "I'm here whenever you're ready.",
        liveLabel: 'Hearing you…',
        frozenLabel: 'You said',
      },
      bertareply: {
        // BertaReplyPanel #428 idle placeholder shown briefly between
        // WS-connect and greeting TTS arriving.
        label: 'Lena',  // shown above the text panel
        idlePlaceholder: "Hello — I'm here whenever you'd like to chat.",
        you: 'You',  // #1032 — heading over the user's own live transcript
      },
      photocarousel: {
        // PhotoCarousel #88 / #432 controls.
        done: 'Done',
        prev: 'Previous photo',
        next: 'Next photo',
        zoomIn: 'Zoom in',
        zoomOut: 'Zoom out',
        help: 'Help',
        closeAria: 'Close photos',
        bertaSaying: 'Lena is saying',
      },
      videooverlay: {
        // VideoOverlay #91 controls.
        done: 'Done',
        play: 'Play',
        pause: 'Pause',
        volumeDown: 'Volume down',
        volumeUp: 'Volume up',
        closeAria: 'Close video',
      },
      musicview: {
        // MusicView #448 full-screen player.
        nowPlaying: 'Now playing',
        fallbackTitle: 'Music',
        play: 'Play',
        pause: 'Pause',
        volumeDown: 'Volume down',
        volumeUp: 'Volume up',
        done: 'Done',
        closeAria: 'Close music player',
      },
      dominogame: {
        // DominoGame #390 UI. Strings with {placeholders} are
        // template-substituted at render time via the formatString
        // helper in i18n/LanguageContext.
        done: 'Done',
        doneAria: 'End the domino game',
        knock: 'Knock (skip)',
        knockAria: 'Knock — skip turn (only when you have no playable tile)',
        // #974 — single-audio-channel domino: Berta no longer SPEAKS
        // her move/pass, so the board shows these cues visually. The
        // "thinking" badge covers the client-side pause before her move
        // renders; "passed" is the only signal her knock happened (a
        // pass changes nothing on the board).
        thinking: '{name} is thinking…',
        passed: '{name} passed',
        bertaStackLabel: '{name} — {count} tiles',     // top of board, opponent stack
        userTilesLabel: 'YOUR TILES — {count} tiles',
        userTilesYourTurn: ' • your turn',             // appended when isCurrentPlayer
        placeOnLeft: 'Place tile on left end of chain',
        placeOnRight: 'Place tile on right end of chain',
        // #551 — empty-chain hint shown in the chain area before the
        // first move. Was hardcoded English; now keyed.
        emptyChainHint: 'Tap a tile from your hand, then tap here to start',
        // #551 — GameEndCard winner / loser / tie copy. Bas's
        // 2026-05-09 staging German repro saw "A perfectly balanced
        // game! Both of us played well." render in English on a
        // German call. Warm + brief copy; no win/lose framing per
        // the design Q-locks.
        endTitleUser:    'What a lovely game!',
        endSubtitleUser: 'You played beautifully.',
        endTitleBerta:    'What a lovely game!',
        endSubtitleBerta: 'Wonderful playing with you.',
        endTitleTie:    'A perfectly balanced game!',
        endSubtitleTie: 'Both of us played well.',
      },
      solitairegame: {
        // SolitaireGame #966 UI — single-player Klondike, visual-only.
        // {placeholders} resolved via formatString. Warm, no win/lose
        // framing; a stuck deal is "luck of the cards", never a loss.
        done: 'Done',
        doneAria: 'End the game of Solitaire',
        undo: 'Undo',
        undoAria: 'Undo the last move',
        stockAria: 'Draw a card from the deck',
        recycleHint: 'Tap to turn the pile over',
        emptyColumnHint: 'Only a King starts an empty column',
        foundationAria: 'Foundation, {suit}',
        stuckTitle: 'The cards aren’t falling our way',
        stuckBody: 'No moves left this hand — it’s just the luck of the cards. Shall we try a fresh deal? Just ask, or tap Done.',
        winTitle: 'What a lovely game!',
        winBody: 'Every card is home. Beautifully played.',
        instructions: 'Tap a card, then tap where it should go. Tap the deck for more cards.',
      },
      gamecarousel: {
        // #1029 — game picker carousel (voice-opened tablet chooser).
        header: 'What shall we play?',
        play: 'Play',
        position: 'Game {index} of {total}',
        favourite: 'You enjoy this',
        closeAria: 'Close the game picker',
        prevAria: 'Previous game',
        nextAria: 'Next game',
        titles: { solitaire: 'Solitaire', domino: 'Dominoes' },
        descriptions: {
          solitaire: 'A calm game of patience with a deck of cards. Play at your own pace.',
          domino: 'Take turns matching tiles end to end. Relaxed and friendly — no score to chase.',
        },
      },
      idledim: {
        // IdleDimOverlay #138 night-mode wake hint.
        nightWakeHint: 'Long-press or double-tap to wake',
      },
      settings: {
        // Settings overlay opened by the gear icon.
        title: 'Settings',
        sectionCompanionStyle: 'Companion style',
        styleSmiley: '😊 Smiley',
        styleWaveform: '📊 Waveform',
        styleTextOnly: '🔤 Text only',
        gearAria: 'Settings',
        refreshAria: 'Refresh the app (long-press to update)',
        // #551 — third section heading inside the settings overlay
        // (mic-test panel). Was a hardcoded English JSX text node.
        sectionMicrophone: 'Microphone',
      },
      idle: {
        // Idle screen — no active call.
        tapToCall: 'Tap to call',
        // #551 — big idle-screen call button (was hardcoded
        // "📞 Call {companionName}").
        callButton: '📞 Call {name}',
      },
      calling: {
        // Calling screen — between tap and connect.
        callingX: 'Calling {name}…',
        // #551 — set as the reply-panel text once the WS handler
        // observes the call has ended (was hardcoded "Call ended").
        callEnded: 'Call ended',
      },
      incoming: {
        // Incoming-call accept/decline.
        callingFrom: 'Incoming call from {name}',
        accept: 'Answer',
        decline: 'Decline',
        // #551 — incoming-call screen had THREE hardcoded strings:
        // the "{name} is calling you" pill, the "Answer" button
        // text, and the "Not now" dismiss link. The first two are
        // now keyed; "Not now" gets its own key (different register
        // from the formal "Decline" used as fallback / aria text).
        callingYou: '{name} is calling you',
        notNow: 'Not now',
      },
      hangup: {
        // Legacy/calling-screen big red button label.
        button: '📵 Hang up',
      },
      // #1089 — aria-label for the standardized ActivityFrame close (X).
      activity: {
        closeAria: 'Close',
      },
      micwarning: {
        // The first mic warning surfaces in the focal area when the
        // user has had multiple empty turns. Phrased as a gentle
        // suggestion, not a demand.
        cantHearWell: "I can't hear you very well — could you speak a little louder?",
        // #544 — first-empty-turn reassurance shown in the reply
        // panel for 4s on the first empty turn (#94 mechanic). Soft,
        // patient register — the user might just be thinking.
        takeYourTime: "Don't worry, take your time",
        // #544 — stronger 3+-empty-turn warning surfaced as a
        // persistent banner. Indicates a likely mic / hardware
        // problem rather than user pacing; phrased so the elderly
        // user knows to check the device.
        checkMicrophone: "Having trouble hearing you — check your microphone",
        // #551 — getUserMedia error → setMicWarning calls. Three
        // distinct copies for permission, hardware, and generic
        // failure so the user knows which knob to turn.
        accessDenied: 'Microphone access needed. Please allow it in your browser settings.',
        notFound: 'No microphone found. Please connect a microphone.',
        generic: 'Microphone error — please try again',
      },
      // #551 — settings-overlay microphone test panel + section
      // label. Strings were hardcoded English in TabletApp's
      // settings overlay JSX; now keyed under tablet.mictest.* and
      // tablet.settings.sectionMicrophone.
      mictest: {
        idle:      '🎤 Test microphone',
        recording: '⏹ Stop and check',
        checking:  '⏳ Checking…',
        // {text} is the transcribed words the server heard back.
        weHeard:   '✅ We heard: "{text}"',
        notHeard:  "❌ We couldn't hear you clearly",
      },
      // Audio self-test (record-and-playback). Captures through the live
      // PCM path so the recording is byte-identical to what the server
      // receives, then plays it back so any quality loss is audible
      // client-side. Diagnostic, but localised like the rest of the
      // settings overlay (rule 6).
      selftest: {
        section:     '🎧 Audio self-test',
        record:      '🔴 Record',
        recordAgain: '🔴 Record again',
        stop:        '⏹ Stop recording',
        play:        '▶️ Play back',
        playing:     '🔊 Playing…',
        hint:        'Records exactly what is sent to the server, then plays it back so you can hear the captured quality.',
        error:       '❌ Recording failed — high-fidelity (PCM) capture is unavailable in this browser.',
        raw:          '🎛 Raw mic (no noise-suppression / AGC)',
        rawHint:      'Off = exactly what calls send. On = unprocessed mic, to compare tone / warmth.',
        tagRaw:       'raw',
        tagProcessed: 'as sent',
      },
      // #551 — domino_error WS-message handler set the reply panel
      // to a fallback string when the server-supplied message was
      // missing. Now keyed.
      domino: {
        moveError: "That move didn't work — try another tile.",
      },
      // #551 — tablet activation / setup screens (rendered before
      // the call UI loads when the tablet has no userId yet, when
      // QR-code activation is in flight, or when activation
      // failed). Strings were hardcoded English.
      setup: {
        activatingTitle: 'Setting up your tablet…',
        activatingBody:  'Please wait while we link this tablet to your account.',
        failedTitle:     'Setup failed',
        // {strongOpen}/{strongClose} markers preserve the inline
        // <strong>family portal</strong> emphasis without forcing
        // every translator to handle React-element substitution.
        // The component renders {strongOpen}…{strongClose} as a
        // <strong> element via formatString-aware splitting.
        failedBody:      'Please go back to the {strongOpen}family portal{strongClose} and scan a new QR code.',
        notSetUpTitle:   'This tablet is not set up yet',
        notSetUpBody:    'Ask a family member to open the Amigo Mio portal and scan the QR code with this tablet.',
        confirmTitle:     'Set up this tablet?',
        confirmBody:      'This tablet will be set up for {name}, with {companion} as their companion.',
        confirmBodyNoName:'This tablet will be set up for your loved one.',
        confirmButton:    'Set up this tablet',
        confirmCancel:    'Not now',
      },
    },
  },

  nl: {
    hero: {
      headline: 'Ze praat elke ochtend met Lena.',
      subline:
        'Amigo Mio geeft uw dierbare een gezel die echt luistert — en nooit vergeet.',
      cta: 'Maak een profiel aan',
    },
    selfWedge: {
      headline: 'Wilt u het voor uzelf instellen?',
      subline:
        'Ja, dat kan ook. Maak uw eigen account aan; de vragen passen zich aan.',
      cta: 'Maak mijn eigen account aan',
    },
    values: [
      {
        icon: '🎙️',
        heading: 'Praat over alles',
        description:
          'Voetbal, herinneringen, het nieuws, een zware dag. Amigo Mio oordeelt nooit en verandert nooit van onderwerp.',
      },
      {
        icon: '🧠',
        heading: 'Onthoudt wat telt',
        description:
          'Elke naam, elk verhaal, elke voorkeur. Uw dierbare hoeft zichzelf nooit te herhalen.',
      },
      {
        icon: '👨‍👩‍👧',
        heading: 'Houdt het gezin dichtbij',
        description:
          'U ziet hoe het met hen gaat — zonder ooit in hun privéwereld te dringen.',
      },
    ],
    nonprofit: {
      badge: 'Non-profit',
      text: 'Amigo Mio is een non-profit project. We houden de kosten zo laag mogelijk — en de eerste maand is volledig gratis.',
      trial: '1 maand gratis · Geen creditcard nodig',
    },
    nav: {
      signIn: 'Inloggen',
      createProfile: 'Maak een profiel aan',
    },
    footer: {
      alreadyHaveAccount: 'Al een account?',
      signIn: 'Inloggen',
    },
    relations: {
      aunt: 'Tante',
      boyfriend: 'Vriend',
      brother: 'Broer',
      caregiver: 'Verzorger',
      daughter: 'Dochter',
      ex: 'Ex',
      father: 'Vader',
      girlfriend: 'Vriendin',
      grandchild: 'Kleinkind',
      mother: 'Moeder',
      nephew: 'Neef',
      niece: 'Nicht',
      parent: 'Ouder',
      partner: 'Partner',
      sibling: 'Broer of zus',
      sister: 'Zus',
      son: 'Zoon',
      spouse: 'Echtgenoot/echtgenote',
      uncle: 'Oom',
      'family member': 'Ander familielid',
    },
    signup: {
      title: 'Profiel aanmaken',
      relationLabel: 'Ik ben de … van de oudere',
      relationPlaceholder: '— kies —',
      change: '← Wijzigen',
      terms: { agree: 'Ik ga akkoord met de', link: 'AmigoMio-voorwaarden', read: '(lezen)', hide: '(verbergen)', required: 'Accepteer de AmigoMio-voorwaarden om een account aan te maken.' },
      name: 'Volledige naam',
      email: 'E-mailadres',
      password: 'Wachtwoord',
      confirmPassword: 'Wachtwoord bevestigen',
      submit: 'Account aanmaken',
      haveAccount: 'Al een account?',
      signIn: 'Inloggen',
      choice: {
        title: 'Voor wie is dit?',
        subtitle: 'Kies wat bij u past. U kunt op het volgende scherm nog wisselen.',
        forLoved: '👵  Voor een dierbare',
        forSelf: '🧓  Voor mijzelf',
      },
      errors: {
        passwordMismatch: 'Wachtwoorden komen niet overeen.',
        generic: 'Er is iets misgegaan. Probeer het opnieuw.',
        emailExists: 'Er bestaat al een account met dit e-mailadres.',
      },
    },
    login: {
      title: 'Inloggen',
      email: 'E-mailadres',
      password: 'Wachtwoord',
      submit: 'Inloggen',
      noAccount: 'Nog geen account?',
      signUp: 'Aanmelden',
      errors: {
        invalid: 'Ongeldig e-mailadres of wachtwoord.',
        generic: 'Er is iets misgegaan. Probeer het opnieuw.',
      },
    },
    intake: {
      title: 'Maak kennis met Mia',
      subtitle: 'Ik help je een profiel aan te maken voor je dierbare. Het duurt ongeveer 5 minuten — je kunt praten of typen.',
      talkWithMia: 'Praten met Mia',
      chatWithMia: 'Chatten met Mia',
      question: 'Vraag',
      of: 'van',
      switchToVoice: 'Overschakelen naar stem',
      switchToText: 'Overschakelen naar tekst',
      inputPlaceholder: 'Typ je antwoord…',
      tapToSpeak: 'Tik om te spreken',
      tapToSend: 'Tik om te verzenden',
      voiceConnecting: 'Verbinding maken met Mia…',
      voiceReady: 'Spreek maar — ik luister.',
      voiceListening: 'Mia spreekt…',
      voiceRecording: 'Luisteren…',
      voiceProcessing: 'Bezig met verwerken…',
      voiceDone: 'Klaar! Je wordt doorgestuurd naar je dashboard…',
      voiceError: 'Verbindingsfout. Ververs de pagina.',
      voiceMicError: 'Toegang tot microfoon geweigerd.',
      errorStart: 'Er is iets misgegaan. Ververs de pagina en probeer opnieuw.',
      errorGeneric: 'Er is iets misgegaan. Probeer het opnieuw.',
      callPrompt: 'Als je klaar bent, druk op Start.',
      connecting: 'Verbinding maken…',
      miaSpeaking: 'Mia spreekt…',
      connectError: 'Kan geen verbinding maken. Ververs de pagina en probeer opnieuw.',
      startBtn: 'Start',
      startCall: 'Start',
      endCall: 'Gesprek beëindigen',
      startOver: 'Opnieuw beginnen',
    },
    portal: {
      welcome: 'Welkom',
      logout: 'Uitloggen',
      startIntake: 'Start intake met Mia',
      sections: {
        profile: { title: 'Profiel', description: 'Bekijk en bewerk het profiel van uw dierbare' },
        interests: { title: 'Interesses & Onderwerpen', description: 'Beheer interesses en geblokkeerde onderwerpen' },
        family: { title: 'Familieleden', description: 'Voeg familieleden toe en beheer ze' },
        friends: { title: 'Vrienden', description: 'Bekijk en beheer vriendschappen' },
        health: { title: 'Gezondheidsnotities', description: 'Privé gezondheids- en geheugennotities' },
        photos: { title: "Foto's", description: "Familiefotos uploaden" },
        alerts: { title: 'Waarschuwingen', description: 'Beschermingsmeldingen voor uw dierbare' },
        social: {
          title: 'Sociale Media', description: 'Facebook, YouTube, RSS-feeds en familie-updates',
          family_updates: 'Familie-updates',
          family_updates_desc: 'Deel iets wat er in de familie is gebeurd — Lena noemt het in het gesprek.',
          person_name_placeholder: 'Over wie gaat dit? (bijv. Carla)',
          relationship_placeholder: 'Relatie (bijv. dochter, kleinzoon)',
          what_happened_placeholder: 'Wat is er gebeurd? Schrijf een update...',
          share_update: 'Update delen',
          youtube_channels: 'YouTube-kanalen',
          youtube_desc: 'Voeg een YouTube-kanaal toe — Lena kan nieuwe video\'s noemen.',
          channel_name_placeholder: 'Kanaalnaam (bijv. Carla\'s kookkanaal)',
          channel_url_placeholder: 'YouTube-URL of handle (bijv. @carla)',
          add_channel: 'Kanaal toevoegen',
          rss_advanced: 'Geavanceerd: RSS-feed',
        },
        news: { title: 'Nieuwsbriefing', description: 'Interessegericht nieuws voor gesprekken' },
        reminders: { title: 'Herinneringen', description: 'Stel herinneringen en verjaardagen in' },
        calls: { title: 'Proactieve oproepen', description: 'Plan check-in oproepen van de metgezel' },
        companion: { title: 'Metgezelinstellingen', description: 'Stel in hoe de metgezel eruitziet, klinkt en zich gedraagt' },
        personality: {
          title: 'Persoonlijkheid',
          nurturer: 'Verzorger', straightTalker: 'Rechttoe-rechtaan', joker: 'Grapjas', intellectual: 'Intellectueel', listener: 'Luisteraar',
          warmth: 'Warmte', humour: 'Humor', pace: 'Tempo', formality: 'Formaliteit', proactivity: 'Proactiviteit',
          warmthLeft: 'Warm & zorgzaam', warmthRight: 'Direct',
          humourLeft: 'Speels & grappig', humourRight: 'Kalm & serieus',
          paceLeft: 'Praatgraag & uitgebreid', paceRight: 'Beknopt',
          formalityLeft: 'Informeel & vriendelijk', formalityRight: 'Formeel & respectvol',
          proactivityLeft: 'Neemt initiatief', proactivityRight: 'Volgt de gebruiker',
          preview: 'Met deze instellingen zal {name} zijn:',
          custom: 'Aangepast',
        },
        activity: { title: 'Gespreksactiviteit', description: 'Recente gesprekssamenvattingen' },
        wellbeing: {
          title: 'Welzijn',
          description: 'Emotioneel welzijnsoverzicht uit gesprekken',
          intro: 'Zo heeft {name} zich gevoeld tijdens gesprekken met {companion}',
          period: 'Periode',
          mood: 'Stemming',
          lastCall: 'Laatste gesprek',
          thisWeek: 'Deze week',
          lastWeek: 'Vorige week',
          lastMonth: 'Afgelopen maand',
          noCalls: 'Geen gesprekken',
          noData: 'Nog geen gespreksgegevens beschikbaar.',
          moods: { happy: 'Blij', neutral: 'Neutraal', low: 'Somber' },
          periodMoods: { happy: 'Overwegend positief', neutral: 'Gemengd', low: 'Overwegend somber' },
          privacyNote: 'Gebaseerd op gesprekstoon \u2014 er worden geen details gedeeld',
        },
        gifts: {
          title: 'Cadeau-idee\u00ebn',
          description: 'Gepersonaliseerde cadeausuggesties',
          refresh: 'Suggesties vernieuwen',
          empty: 'Voeg interesses toe om cadeau-idee\u00ebn te krijgen',
        },
        pets: { title: 'Huisdieren', description: 'Huisdieren en de verhalen eromheen' },
        faith: { title: 'Geloof', description: 'Geloof, tradities en spirituele beleving' },
        childhood: { title: 'Jeugd', description: 'Verhalen en herinneringen uit de jonge jaren' },
        work: { title: 'Werkend leven', description: 'Loopbaan en waar ze trots op waren' },
        account: { title: 'Account', description: 'Instellingen voor je gezinsaccount' },
      },
      tablet: {
        close_photos: 'Sluiten',
      },
      comingSoon: 'Binnenkort beschikbaar',
      detail: {
        backToDashboard: 'Terug naar dashboard',
        loading: 'Laden…',
        noIntakeTitle: 'Nog geen intake afgerond',
        noIntakeText: 'Rond de intake af met Mia om deze sectie in te vullen.',
        fetchError: 'Kon gegevens niet laden. Probeer het opnieuw.',
        name: 'Naam',
        nickname: 'Bijnaam',
        age: 'Leeftijd',
        language: 'Taal',
        location: 'Locatie',
        noInterests: 'Nog geen interesses vastgelegd.',
        noFamily: 'Nog geen familieleden vastgelegd.',
        noFriends: 'Nog geen vrienden toegevoegd.',
        noHealth: 'Geen gezondheidsnotities vastgelegd.',
        familyOnly: 'Alleen zichtbaar voor familie',
        addReminder: 'Herinnering toevoegen',
        reminderPlaceholder: 'bijv. Bel Sophie, neem pillen...',
        birthdayName: 'Naam',
        birthdayDate: 'Datum (MM-DD)',
        addBirthday: 'Verjaardag toevoegen',
        pending: 'Openstaand',
        completed: 'Afgerond',
        noReminders: 'Nog geen herinneringen.',
        dueDate: 'Vervaldatum',
        optional: 'optioneel',
      },
    },
    tablet: {
      callstrip: {
        status: {
          idle: '',
          wake_listening: 'Wakker worden',
          listening: 'Aan het luisteren',
          transcribing: 'Even denken',
          thinking: 'Even denken',
          speaking: 'Aan het praten',
          reconnecting: 'Opnieuw verbinden',
          error: 'Er ging iets mis',
          quota: 'Even rusten',
          music_playing: 'Muziek speelt',
          // #687 — wait mode label (NL).
          waiting: 'Even wachten',
        },
        endCall: 'Gesprek beëindigen',
        hiccup: 'Kleine hapering',
        tryingAgain: 'Probeer het opnieuw',
        mutedMicLabel: 'microfoon gedempt',
        // #551
        youSaidLabel: 'Je zei',
        hiccupTooltip: 'Verbindingshapering — de laatste beurt kwam niet schoon door bij de server.',
        // #700 — pause/resume button labels (NL).
        pauseLabel: 'Even stil',
        resumeLabel: 'Verder gaan',
      },
      yousaid: {
        placeholderTitle: 'Zeg gerust iets',
        placeholderBody: 'Ik ben er wanneer u klaar bent.',
        liveLabel: 'Ik luister…',
        frozenLabel: 'U zei',
      },
      bertareply: {
        label: 'Lena',
        idlePlaceholder: 'Hallo — ik ben er wanneer u wilt praten.',
        you: 'Jij',  // #1032
      },
      photocarousel: {
        done: 'Klaar',
        prev: 'Vorige foto',
        next: 'Volgende foto',
        zoomIn: 'Inzoomen',
        zoomOut: 'Uitzoomen',
        help: 'Hulp',
        closeAria: "Foto's sluiten",
        bertaSaying: 'Lena zegt',
      },
      videooverlay: {
        done: 'Klaar',
        play: 'Afspelen',
        pause: 'Pauze',
        volumeDown: 'Volume omlaag',
        volumeUp: 'Volume omhoog',
        closeAria: 'Video sluiten',
      },
      musicview: {
        nowPlaying: 'Nu aan het spelen',
        fallbackTitle: 'Muziek',
        play: 'Afspelen',
        pause: 'Pauze',
        volumeDown: 'Volume omlaag',
        volumeUp: 'Volume omhoog',
        done: 'Klaar',
        closeAria: 'Muziekspeler sluiten',
      },
      dominogame: {
        done: 'Klaar',
        doneAria: 'Het dominospel beëindigen',
        knock: 'Kloppen (overslaan)',
        knockAria: 'Kloppen — beurt overslaan (alleen als u geen speelbare steen heeft)',
        thinking: '{name} denkt na…',
        passed: '{name} paste',
        bertaStackLabel: '{name} — {count} stenen',
        userTilesLabel: 'UW STENEN — {count} stenen',
        userTilesYourTurn: ' • uw beurt',
        placeOnLeft: 'Steen aan linkerkant van de rij plaatsen',
        placeOnRight: 'Steen aan rechterkant van de rij plaatsen',
        // #551
        emptyChainHint: 'Tik op een steen uit uw hand en daarna hier om te beginnen',
        endTitleUser:    'Wat een mooi spel!',
        endSubtitleUser: 'U speelde prachtig.',
        endTitleBerta:    'Wat een mooi spel!',
        endSubtitleBerta: 'Heerlijk om met u te spelen.',
        endTitleTie:    'Een prachtig gelijkspel!',
        endSubtitleTie: 'We speelden allebei goed.',
      },
      solitairegame: {
        done: 'Klaar',
        doneAria: 'Het patiencespel beëindigen',
        undo: 'Ongedaan maken',
        undoAria: 'De laatste zet ongedaan maken',
        stockAria: 'Een kaart van de stapel trekken',
        recycleHint: 'Tik om de stapel om te draaien',
        emptyColumnHint: 'Alleen een Heer mag een lege kolom beginnen',
        foundationAria: 'Stapel, {suit}',
        stuckTitle: 'De kaarten zitten ons even tegen',
        stuckBody: 'Geen zetten meer deze keer — dat is gewoon het geluk van de kaarten. Zullen we opnieuw delen? Zeg het maar, of tik op Klaar.',
        winTitle: 'Wat een mooi spel!',
        winBody: 'Elke kaart op zijn plek. Prachtig gespeeld.',
        instructions: 'Tik op een kaart en dan waar die heen moet. Tik op de stapel voor meer kaarten.',
      },
      gamecarousel: {
        header: 'Wat zullen we spelen?',
        play: 'Spelen',
        position: 'Spel {index} van {total}',
        favourite: 'Dit vind je leuk',
        closeAria: 'De spelkeuze sluiten',
        prevAria: 'Vorig spel',
        nextAria: 'Volgend spel',
        titles: { solitaire: 'Patience', domino: 'Domino' },
        descriptions: {
          solitaire: 'Een rustig spelletje patience met een kaartspel. Speel op je eigen tempo.',
          domino: 'Om de beurt stenen aan elkaar leggen. Ontspannen en gezellig — geen score om te jagen.',
        },
      },
      idledim: {
        nightWakeHint: 'Lang indrukken of dubbeltikken om te wekken',
      },
      settings: {
        title: 'Instellingen',
        sectionCompanionStyle: 'Stijl van de metgezel',
        styleSmiley: '😊 Smiley',
        styleWaveform: '📊 Golfvorm',
        styleTextOnly: '🔤 Alleen tekst',
        gearAria: 'Instellingen',
        refreshAria: 'App verversen (lang ingedrukt houden)',
        // #551
        sectionMicrophone: 'Microfoon',
      },
      idle: {
        tapToCall: 'Tik om te bellen',
        // #551
        callButton: '📞 Bel {name}',
      },
      calling: {
        callingX: '{name} bellen…',
        // #551
        callEnded: 'Gesprek beëindigd',
      },
      incoming: {
        callingFrom: 'Inkomend gesprek van {name}',
        accept: 'Opnemen',
        decline: 'Weigeren',
        // #551
        callingYou: '{name} belt u',
        notNow: 'Nu niet',
      },
      hangup: {
        button: '📵 Ophangen',
      },
      activity: {
        closeAria: 'Sluiten',
      },
      micwarning: {
        cantHearWell: 'Ik kan u niet zo goed horen — kunt u iets harder spreken?',
        // #544 — first-empty-turn reassurance, informal-tu register
        // matching the rest of the Dutch tablet copy.
        takeYourTime: 'Geen zorgen, neem rustig de tijd',
        // #544 — 3+-empty-turn mic-hardware warning.
        checkMicrophone: 'Ik versta je moeilijk — controleer even de microfoon',
        // #551
        accessDenied: 'Toegang tot de microfoon nodig. Sta dit toe in uw browserinstellingen.',
        notFound: 'Geen microfoon gevonden. Sluit een microfoon aan.',
        generic: 'Microfoonfout — probeer het opnieuw',
      },
      mictest: {
        idle:      '🎤 Microfoon testen',
        recording: '⏹ Stoppen en controleren',
        checking:  '⏳ Bezig met controleren…',
        weHeard:   '✅ We hoorden: "{text}"',
        notHeard:  '❌ We konden u niet duidelijk verstaan',
      },
      selftest: {
        section:     '🎧 Audio-zelftest',
        record:      '🔴 Opnemen',
        recordAgain: '🔴 Opnieuw opnemen',
        stop:        '⏹ Opname stoppen',
        play:        '▶️ Afspelen',
        playing:     '🔊 Aan het afspelen…',
        hint:        'Neemt precies op wat naar de server wordt gestuurd en speelt het daarna af, zodat u de opgenomen kwaliteit kunt beoordelen.',
        error:       '❌ Opname mislukt — hifi-opname (PCM) is niet beschikbaar in deze browser.',
        raw:          '🎛 Onbewerkte microfoon (geen ruisonderdrukking / AGC)',
        rawHint:      'Uit = precies wat gesprekken versturen. Aan = onbewerkte microfoon, om de toon / warmte te vergelijken.',
        tagRaw:       'onbewerkt',
        tagProcessed: 'zoals verstuurd',
      },
      domino: {
        moveError: 'Die zet werkte niet — probeer een andere steen.',
      },
      setup: {
        activatingTitle: 'Tablet wordt ingesteld…',
        activatingBody:  'Een ogenblik — we koppelen deze tablet aan uw account.',
        failedTitle:     'Instellen mislukt',
        failedBody:      'Ga terug naar het {strongOpen}familieportaal{strongClose} en scan een nieuwe QR-code.',
        notSetUpTitle:   'Deze tablet is nog niet ingesteld',
        notSetUpBody:    'Vraag een familielid om het Amigo Mio-portaal te openen en de QR-code met deze tablet te scannen.',
        confirmTitle:     'Deze tablet instellen?',
        confirmBody:      'Deze tablet wordt ingesteld voor {name}, met {companion} als metgezel.',
        confirmBodyNoName:'Deze tablet wordt ingesteld voor uw dierbare.',
        confirmButton:    'Deze tablet instellen',
        confirmCancel:    'Niet nu',
      },
    },
  },

  es: {
    hero: {
      headline: 'Ella habla con Lena cada mañana.',
      subline:
        'Amigo Mio le da a tu ser querido un compañero que realmente escucha — y nunca olvida.',
      cta: 'Crear un perfil',
    },
    selfWedge: {
      headline: '¿Lo quiere configurar para usted mismo?',
      subline:
        'Sí, también es posible. Cree su propia cuenta; las preguntas se adaptan.',
      cta: 'Crear mi propia cuenta',
    },
    values: [
      {
        icon: '🎙️',
        heading: 'Habla de todo',
        description:
          'Fútbol, recuerdos, las noticias, un día difícil. Amigo Mio nunca juzga y nunca cambia de tema.',
      },
      {
        icon: '🧠',
        heading: 'Recuerda lo que importa',
        description:
          'Cada nombre, cada historia, cada preferencia. Tu ser querido nunca tendrá que repetirse.',
      },
      {
        icon: '👨‍👩‍👧',
        heading: 'Mantiene a la familia cerca',
        description:
          'Verás cómo están — sin entrometerte nunca en sus momentos privados.',
      },
    ],
    nonprofit: {
      badge: 'Sin ánimo de lucro',
      text: 'Amigo Mio es un proyecto sin ánimo de lucro. Mantenemos los costes lo más bajos posible — y el primer mes es completamente gratis.',
      trial: '1 mes gratis · Sin tarjeta de crédito',
    },
    nav: {
      signIn: 'Iniciar sesión',
      createProfile: 'Crear un perfil',
    },
    footer: {
      alreadyHaveAccount: '¿Ya tienes cuenta?',
      signIn: 'Iniciar sesión',
    },
    relations: {
      aunt: 'Tía',
      boyfriend: 'Novio',
      brother: 'Hermano',
      caregiver: 'Cuidador',
      daughter: 'Hija',
      ex: 'Ex',
      father: 'Padre',
      girlfriend: 'Novia',
      grandchild: 'Nieto/a',
      mother: 'Madre',
      nephew: 'Sobrino',
      niece: 'Sobrina',
      parent: 'Padre/madre',
      partner: 'Pareja',
      sibling: 'Hermano/a',
      sister: 'Hermana',
      son: 'Hijo',
      spouse: 'Cónyuge',
      uncle: 'Tío',
      'family member': 'Otro familiar',
    },
    signup: {
      title: 'Crear un perfil',
      relationLabel: 'Soy el/la … de la persona mayor',
      relationPlaceholder: '— seleccionar —',
      change: '← Cambiar',
      terms: { agree: 'Acepto los', link: 'Términos de AmigoMio', read: '(leer)', hide: '(ocultar)', required: 'Acepta los Términos de AmigoMio para crear una cuenta.' },
      name: 'Nombre completo',
      email: 'Correo electrónico',
      password: 'Contraseña',
      confirmPassword: 'Confirmar contraseña',
      submit: 'Crear cuenta',
      haveAccount: '¿Ya tienes cuenta?',
      signIn: 'Iniciar sesión',
      choice: {
        title: '¿Para quién es esto?',
        subtitle: 'Elija lo que le encaja. Puede cambiar de opinión en la siguiente pantalla.',
        forLoved: '👵  Para un ser querido',
        forSelf: '🧓  Para mí mismo',
      },
      errors: {
        passwordMismatch: 'Las contraseñas no coinciden.',
        generic: 'Algo salió mal. Inténtalo de nuevo.',
        emailExists: 'Ya existe una cuenta con este correo electrónico.',
      },
    },
    login: {
      title: 'Iniciar sesión',
      email: 'Correo electrónico',
      password: 'Contraseña',
      submit: 'Iniciar sesión',
      noAccount: '¿No tienes cuenta?',
      signUp: 'Regístrate',
      errors: {
        invalid: 'Correo o contraseña incorrectos.',
        generic: 'Algo salió mal. Inténtalo de nuevo.',
      },
    },
    intake: {
      title: 'Conoce a Mia',
      subtitle: 'Te ayudaré a crear un perfil para tu ser querido. Tarda unos 5 minutos — puedes hablar o escribir.',
      talkWithMia: 'Hablar con Mia',
      chatWithMia: 'Chatear con Mia',
      question: 'Pregunta',
      of: 'de',
      switchToVoice: 'Cambiar a voz',
      switchToText: 'Cambiar a texto',
      inputPlaceholder: 'Escribe tu respuesta…',
      tapToSpeak: 'Toca para hablar',
      tapToSend: 'Toca para enviar',
      voiceConnecting: 'Conectando con Mia…',
      voiceReady: 'Habla — te estoy escuchando.',
      voiceListening: 'Mia está hablando…',
      voiceRecording: 'Escuchando…',
      voiceProcessing: 'Procesando…',
      voiceDone: '¡Todo listo! Llevándote a tu panel…',
      voiceError: 'Error de conexión. Por favor, recarga la página.',
      voiceMicError: 'Acceso al micrófono denegado.',
      errorStart: 'Algo salió mal. Recarga la página e inténtalo de nuevo.',
      errorGeneric: 'Algo salió mal. Inténtalo de nuevo.',
      callPrompt: 'Cuando estés listo, pulsa Iniciar.',
      connecting: 'Conectando…',
      miaSpeaking: 'Mia está hablando…',
      connectError: 'No se pudo conectar. Recarga la página e inténtalo de nuevo.',
      startBtn: 'Iniciar',
      startCall: 'Iniciar',
      endCall: 'Finalizar llamada',
      startOver: 'Empezar de nuevo',
    },
    portal: {
      welcome: 'Bienvenido',
      logout: 'Cerrar sesión',
      startIntake: 'Iniciar entrevista con Mia',
      sections: {
        profile: { title: 'Perfil', description: 'Ver y editar el perfil de tu ser querido' },
        interests: { title: 'Intereses y temas', description: 'Gestionar intereses y temas bloqueados' },
        family: { title: 'Miembros de la familia', description: 'Añadir y gestionar conexiones familiares' },
        friends: { title: 'Amigos', description: 'Ver y gestionar amistades' },
        health: { title: 'Notas de salud', description: 'Notas privadas de salud y memoria' },
        photos: { title: 'Fotos', description: 'Subir fotos familiares' },
        alerts: { title: 'Alertas', description: 'Alertas de protección para tu ser querido' },
        social: {
          title: 'Redes Sociales', description: 'Facebook, YouTube, feeds RSS y actualizaciones familiares',
          family_updates: 'Actualizaciones familiares',
          family_updates_desc: 'Comparte algo que pasó en la familia — Lena lo mencionará en la conversación.',
          person_name_placeholder: '¿De quién se trata? (ej. Carla)',
          relationship_placeholder: 'Relación (ej. hija, nieto)',
          what_happened_placeholder: '¿Qué pasó? Escribe una actualización...',
          share_update: 'Compartir actualización',
          youtube_channels: 'Canales de YouTube',
          youtube_desc: 'Añade un canal de YouTube — Lena puede mencionar nuevos videos.',
          channel_name_placeholder: 'Nombre del canal (ej. canal de cocina de Carla)',
          channel_url_placeholder: 'URL o nombre de YouTube (ej. @carla)',
          add_channel: 'Añadir canal',
          rss_advanced: 'Avanzado: feed RSS',
        },
        news: { title: 'Resumen de noticias', description: 'Noticias según intereses para conversaciones' },
        reminders: { title: 'Recordatorios', description: 'Configura recordatorios y cumpleaños' },
        calls: { title: 'Llamadas proactivas', description: 'Programa llamadas de seguimiento del compañero' },
        companion: { title: 'Ajustes del compañero', description: 'Configura cómo se ve, suena y se comporta el compañero' },
        personality: {
          title: 'Personalidad',
          nurturer: 'Cuidador', straightTalker: 'Directo', joker: 'Bromista', intellectual: 'Intelectual', listener: 'Oyente',
          warmth: 'Calidez', humour: 'Humor', pace: 'Ritmo', formality: 'Formalidad', proactivity: 'Proactividad',
          warmthLeft: 'Cálido y cariñoso', warmthRight: 'Directo',
          humourLeft: 'Juguetón y divertido', humourRight: 'Tranquilo y serio',
          paceLeft: 'Hablador y expansivo', paceRight: 'Conciso',
          formalityLeft: 'Informal y amigable', formalityRight: 'Formal y respetuoso',
          proactivityLeft: 'Toma la iniciativa', proactivityRight: 'Sigue al usuario',
          preview: 'Con estos ajustes, {name} será:',
          custom: 'Personalizado',
        },
        activity: { title: 'Actividad de conversación', description: 'Resúmenes recientes de conversaciones' },
        wellbeing: {
          title: 'Bienestar',
          description: 'Resumen de bienestar emocional de las conversaciones',
          intro: 'Así se ha sentido {name} durante sus conversaciones con {companion}',
          period: 'Período',
          mood: 'Estado de ánimo',
          lastCall: 'Última llamada',
          thisWeek: 'Esta semana',
          lastWeek: 'Semana pasada',
          lastMonth: 'Último mes',
          noCalls: 'Sin llamadas',
          noData: 'Aún no hay datos de conversación disponibles.',
          moods: { happy: 'Contento', neutral: 'Neutral', low: 'Bajo' },
          periodMoods: { happy: 'Mayormente positivo', neutral: 'Mixto', low: 'Mayormente bajo' },
          privacyNote: 'Basado solo en el tono de la conversación \u2014 no se comparten detalles',
        },
        gifts: {
          title: 'Ideas de regalo',
          description: 'Sugerencias de regalos personalizadas',
          refresh: 'Actualizar sugerencias',
          empty: 'A\u00f1ade intereses para obtener ideas de regalo',
        },
        pets: { title: 'Mascotas', description: 'Mascotas y las historias a su alrededor' },
        faith: { title: 'Fe', description: 'Fe, tradiciones y pr\u00e1ctica espiritual' },
        childhood: { title: 'Infancia', description: 'Historias y recuerdos de los primeros a\u00f1os' },
        work: { title: 'Vida laboral', description: 'Carrera y aquello de lo que se sent\u00edan orgullosos' },
        account: { title: 'Cuenta', description: 'Ajustes de tu cuenta familiar' },
      },
      tablet: {
        close_photos: 'Cerrar',
      },
      comingSoon: 'Próximamente',
      detail: {
        backToDashboard: 'Volver al panel',
        loading: 'Cargando…',
        noIntakeTitle: 'Aún no se ha completado la entrevista',
        noIntakeText: 'Completa la entrevista con Mia para rellenar esta sección.',
        fetchError: 'No se pudieron cargar los datos. Inténtalo de nuevo.',
        name: 'Nombre',
        nickname: 'Apodo',
        age: 'Edad',
        language: 'Idioma',
        location: 'Ubicación',
        noInterests: 'Aún no se han registrado intereses.',
        noFamily: 'Aún no se han registrado miembros de la familia.',
        noFriends: 'Aún no se han añadido amigos.',
        noHealth: 'No se han registrado notas de salud.',
        familyOnly: 'Visible solo para la familia',
        addReminder: 'Añadir recordatorio',
        reminderPlaceholder: 'ej. Llamar a Sophie, tomar pastillas...',
        birthdayName: 'Nombre',
        birthdayDate: 'Fecha (MM-DD)',
        addBirthday: 'Añadir cumpleaños',
        pending: 'Pendiente',
        completed: 'Completado',
        noReminders: 'Sin recordatorios aún.',
        dueDate: 'Fecha límite',
        optional: 'opcional',
      },
    },
    tablet: {
      callstrip: {
        status: {
          idle: '',
          wake_listening: 'Despertando',
          listening: 'Escuchando',
          transcribing: 'Déjame pensar',
          thinking: 'Déjame pensar',
          speaking: 'Hablando',
          reconnecting: 'Reconectando',
          error: 'Algo salió mal',
          quota: 'Descansando un rato',
          music_playing: 'Sonando música',
          // #687 — wait mode label (ES).
          waiting: 'Esperando',
        },
        endCall: 'Finalizar llamada',
        hiccup: 'Pequeño tropiezo',
        tryingAgain: 'Intentándolo de nuevo',
        mutedMicLabel: 'micrófono silenciado',
        // #551
        youSaidLabel: 'Dijiste',
        hiccupTooltip: 'Pequeño tropiezo de conexión — el último turno no llegó limpio al servidor.',
        // #700 — pause/resume button labels (ES).
        pauseLabel: 'Silencio un momento',
        resumeLabel: 'Continuar',
      },
      yousaid: {
        placeholderTitle: 'Di lo que quieras',
        placeholderBody: 'Estoy aquí cuando estés listo.',
        liveLabel: 'Te escucho…',
        frozenLabel: 'Dijiste',
      },
      bertareply: {
        label: 'Lena',
        idlePlaceholder: 'Hola — estoy aquí cuando quieras charlar.',
        you: 'Tú',  // #1032
      },
      photocarousel: {
        done: 'Listo',
        prev: 'Foto anterior',
        next: 'Foto siguiente',
        zoomIn: 'Acercar',
        zoomOut: 'Alejar',
        help: 'Ayuda',
        closeAria: 'Cerrar fotos',
        bertaSaying: 'Lena dice',
      },
      videooverlay: {
        done: 'Listo',
        play: 'Reproducir',
        pause: 'Pausa',
        volumeDown: 'Bajar volumen',
        volumeUp: 'Subir volumen',
        closeAria: 'Cerrar vídeo',
      },
      musicview: {
        nowPlaying: 'Sonando ahora',
        fallbackTitle: 'Música',
        play: 'Reproducir',
        pause: 'Pausa',
        volumeDown: 'Bajar volumen',
        volumeUp: 'Subir volumen',
        done: 'Listo',
        closeAria: 'Cerrar reproductor de música',
      },
      dominogame: {
        done: 'Listo',
        doneAria: 'Terminar la partida de dominó',
        knock: 'Pasar (saltar)',
        knockAria: 'Pasar — saltar turno (solo cuando no tienes ficha jugable)',
        thinking: '{name} está pensando…',
        passed: '{name} pasó',
        bertaStackLabel: '{name} — {count} fichas',
        userTilesLabel: 'TUS FICHAS — {count} fichas',
        userTilesYourTurn: ' • tu turno',
        placeOnLeft: 'Colocar ficha en el extremo izquierdo de la cadena',
        placeOnRight: 'Colocar ficha en el extremo derecho de la cadena',
        // #551
        emptyChainHint: 'Toca una ficha de tu mano y luego aquí para empezar',
        endTitleUser:    '¡Qué partida tan bonita!',
        endSubtitleUser: 'Has jugado precioso.',
        endTitleBerta:    '¡Qué partida tan bonita!',
        endSubtitleBerta: 'Qué gusto jugar contigo.',
        endTitleTie:    '¡Una partida muy pareja!',
        endSubtitleTie: 'Los dos jugamos bien.',
      },
      solitairegame: {
        done: 'Listo',
        doneAria: 'Terminar el solitario',
        undo: 'Deshacer',
        undoAria: 'Deshacer el último movimiento',
        stockAria: 'Robar una carta del mazo',
        recycleHint: 'Toca para dar la vuelta al montón',
        emptyColumnHint: 'Solo un Rey puede empezar una columna vacía',
        foundationAria: 'Base, {suit}',
        stuckTitle: 'Las cartas no nos acompañan hoy',
        stuckBody: 'No quedan jugadas esta vez — es solo la suerte de las cartas. ¿Probamos un reparto nuevo? Solo tienes que pedirlo, o toca Listo.',
        winTitle: '¡Qué partida tan bonita!',
        winBody: 'Cada carta en su sitio. Has jugado precioso.',
        instructions: 'Toca una carta y luego dónde debe ir. Toca el mazo para más cartas.',
      },
      gamecarousel: {
        header: '¿A qué jugamos?',
        play: 'Jugar',
        position: 'Juego {index} de {total}',
        favourite: 'Esto te gusta',
        closeAria: 'Cerrar el selector de juegos',
        prevAria: 'Juego anterior',
        nextAria: 'Juego siguiente',
        titles: { solitaire: 'Solitario', domino: 'Dominó' },
        descriptions: {
          solitaire: 'Una tranquila partida de solitario con una baraja. Juega a tu ritmo.',
          domino: 'Por turnos, encajando fichas de punta a punta. Relajado y ameno, sin puntuación que perseguir.',
        },
      },
      idledim: {
        nightWakeHint: 'Mantén pulsado o toca dos veces para despertar',
      },
      settings: {
        title: 'Ajustes',
        sectionCompanionStyle: 'Estilo del compañero',
        styleSmiley: '😊 Carita',
        styleWaveform: '📊 Onda',
        styleTextOnly: '🔤 Solo texto',
        gearAria: 'Ajustes',
        refreshAria: 'Refrescar la aplicación (mantén pulsado)',
        // #551
        sectionMicrophone: 'Micrófono',
      },
      idle: {
        tapToCall: 'Toca para llamar',
        // #551
        callButton: '📞 Llamar a {name}',
      },
      calling: {
        callingX: 'Llamando a {name}…',
        // #551
        callEnded: 'Llamada terminada',
      },
      incoming: {
        callingFrom: 'Llamada entrante de {name}',
        accept: 'Contestar',
        decline: 'Rechazar',
        // #551
        callingYou: '{name} te está llamando',
        notNow: 'Ahora no',
      },
      hangup: {
        button: '📵 Colgar',
      },
      activity: {
        closeAria: 'Cerrar',
      },
      micwarning: {
        cantHearWell: 'No te oigo muy bien — ¿podrías hablar un poco más alto?',
        // #544 — first-empty-turn reassurance, informal-tú register.
        takeYourTime: 'Tranquilo, tómate tu tiempo',
        // #544 — 3+-empty-turn mic-hardware warning.
        checkMicrophone: 'No te escucho bien — revisa el micrófono',
        // #551
        accessDenied: 'Necesito acceso al micrófono. Permítelo en los ajustes del navegador.',
        notFound: 'No se encontró ningún micrófono. Conecta uno, por favor.',
        generic: 'Error de micrófono — inténtalo de nuevo',
      },
      mictest: {
        idle:      '🎤 Probar micrófono',
        recording: '⏹ Parar y comprobar',
        checking:  '⏳ Comprobando…',
        weHeard:   '✅ Hemos oído: "{text}"',
        notHeard:  '❌ No te oímos con claridad',
      },
      selftest: {
        section:     '🎧 Autoprueba de audio',
        record:      '🔴 Grabar',
        recordAgain: '🔴 Grabar de nuevo',
        stop:        '⏹ Detener grabación',
        play:        '▶️ Reproducir',
        playing:     '🔊 Reproduciendo…',
        hint:        'Graba exactamente lo que se envía al servidor y luego lo reproduce para que puedas evaluar la calidad capturada.',
        error:       '❌ Error de grabación — la captura de alta fidelidad (PCM) no está disponible en este navegador.',
        raw:          '🎛 Micrófono sin procesar (sin supresión de ruido / AGC)',
        rawHint:      'Desactivado = exactamente lo que envían las llamadas. Activado = micrófono sin procesar, para comparar el tono / la calidez.',
        tagRaw:       'sin procesar',
        tagProcessed: 'tal como se envía',
      },
      domino: {
        moveError: 'Esa jugada no funcionó — prueba con otra ficha.',
      },
      setup: {
        activatingTitle: 'Configurando tu tablet…',
        activatingBody:  'Espera un momento — estamos vinculando esta tablet con tu cuenta.',
        failedTitle:     'Error de configuración',
        failedBody:      'Vuelve al {strongOpen}portal familiar{strongClose} y escanea un nuevo código QR.',
        notSetUpTitle:   'Esta tablet aún no está configurada',
        notSetUpBody:    'Pídele a un familiar que abra el portal de Amigo Mio y escanee el código QR con esta tablet.',
        confirmTitle:     '¿Configurar esta tablet?',
        confirmBody:      'Esta tablet se configurará para {name}, con {companion} como su compañera.',
        confirmBodyNoName:'Esta tablet se configurará para tu ser querido.',
        confirmButton:    'Configurar esta tablet',
        confirmCancel:    'Ahora no',
      },
    },
  },

  it: {
    hero: {
      headline: 'Parla con Lena ogni mattina.',
      subline:
        'Amigo Mio dà al tuo caro un compagno che ascolta davvero — e non dimentica mai.',
      cta: 'Crea un profilo',
    },
    selfWedge: {
      headline: 'Lo sta configurando per sé?',
      subline:
        'Sì, si può anche fare così. Crei il suo profilo; le domande si adattano.',
      cta: 'Crea il mio profilo',
    },
    values: [
      {
        icon: '🎙️',
        heading: 'Parla di tutto',
        description:
          'Calcio, ricordi, le notizie, una giornata difficile. Amigo Mio non giudica mai e non cambia mai argomento.',
      },
      {
        icon: '🧠',
        heading: 'Ricorda ciò che conta',
        description:
          'Ogni nome, ogni storia, ogni preferenza. Il tuo caro non dovrà mai ripetersi.',
      },
      {
        icon: '👨‍👩‍👧',
        heading: 'Tiene la famiglia vicina',
        description:
          'Vedrai come stanno — senza mai intrometterti nei loro momenti privati.',
      },
    ],
    nonprofit: {
      badge: 'No profit',
      text: 'Amigo Mio è un progetto no profit. Manteniamo i costi il più bassi possibile — e il primo mese è completamente gratuito.',
      trial: '1 mese gratis · Nessuna carta di credito',
    },
    nav: {
      signIn: 'Accedi',
      createProfile: 'Crea un profilo',
    },
    footer: {
      alreadyHaveAccount: 'Hai già un account?',
      signIn: 'Accedi',
    },
    relations: {
      aunt: 'Zia',
      boyfriend: 'Fidanzato',
      brother: 'Fratello',
      caregiver: 'Assistente',
      daughter: 'Figlia',
      ex: 'Ex',
      father: 'Padre',
      girlfriend: 'Fidanzata',
      grandchild: 'Nipote (di nonni)',
      mother: 'Madre',
      nephew: 'Nipote di zii (m)',
      niece: 'Nipote di zii (f)',
      parent: 'Genitore',
      partner: 'Partner',
      sibling: 'Fratello/sorella',
      sister: 'Sorella',
      son: 'Figlio',
      spouse: 'Coniuge',
      uncle: 'Zio',
      'family member': 'Altro familiare',
    },
    signup: {
      title: 'Crea un profilo',
      relationLabel: 'Sono il/la … della persona anziana',
      relationPlaceholder: '— seleziona —',
      change: '← Modifica',
      terms: { agree: 'Accetto i', link: 'Termini di AmigoMio', read: '(leggi)', hide: '(nascondi)', required: 'Accetta i Termini di AmigoMio per creare un account.' },
      name: 'Nome completo',
      email: 'Indirizzo email',
      password: 'Password',
      confirmPassword: 'Conferma password',
      submit: 'Crea account',
      haveAccount: 'Hai già un account?',
      signIn: 'Accedi',
      choice: {
        title: 'Per chi è?',
        subtitle: 'Scelga quella che fa per lei. Può cambiare idea nella schermata successiva.',
        forLoved: '👵  Per una persona cara',
        forSelf: '🧓  Per me',
      },
      errors: {
        passwordMismatch: 'Le password non corrispondono.',
        generic: 'Qualcosa è andato storto. Riprova.',
        emailExists: 'Esiste già un account con questa email.',
      },
    },
    login: {
      title: 'Accedi',
      email: 'Indirizzo email',
      password: 'Password',
      submit: 'Accedi',
      noAccount: 'Non hai un account?',
      signUp: 'Registrati',
      errors: {
        invalid: 'Email o password non validi.',
        generic: 'Qualcosa è andato storto. Riprova.',
      },
    },
    intake: {
      title: 'Incontra Mia',
      subtitle: 'Ti aiuterò a creare un profilo per il tuo caro. Ci vogliono circa 5 minuti — puoi parlare o scrivere.',
      talkWithMia: 'Parla con Mia',
      chatWithMia: 'Chatta con Mia',
      question: 'Domanda',
      of: 'di',
      switchToVoice: 'Passa alla voce',
      switchToText: 'Passa al testo',
      inputPlaceholder: 'Scrivi la tua risposta…',
      tapToSpeak: 'Tocca per parlare',
      tapToSend: 'Tocca per inviare',
      voiceConnecting: 'Connessione a Mia…',
      voiceReady: 'Parla pure — ti sto ascoltando.',
      voiceListening: 'Mia sta parlando…',
      voiceRecording: 'In ascolto…',
      voiceProcessing: 'Elaborazione…',
      voiceDone: 'Tutto fatto! Ti porto alla tua dashboard…',
      voiceError: 'Errore di connessione. Aggiorna la pagina.',
      voiceMicError: 'Accesso al microfono negato.',
      errorStart: 'Qualcosa è andato storto. Aggiorna e riprova.',
      errorGeneric: 'Qualcosa è andato storto. Riprova.',
      callPrompt: 'Quando sei pronto, premi Avvia.',
      connecting: 'Connessione in corso…',
      miaSpeaking: 'Mia sta parlando…',
      connectError: 'Impossibile connettersi. Aggiorna la pagina e riprova.',
      startBtn: 'Avvia',
      startCall: 'Avvia',
      endCall: 'Termina chiamata',
      startOver: 'Ricomincia',
    },
    portal: {
      welcome: 'Benvenuto',
      logout: 'Esci',
      startIntake: 'Inizia il colloquio con Mia',
      sections: {
        profile: { title: 'Profilo', description: 'Visualizza e modifica il profilo del tuo caro' },
        interests: { title: 'Interessi e argomenti', description: 'Gestisci interessi e argomenti bloccati' },
        family: { title: 'Familiari', description: 'Aggiungi e gestisci i contatti familiari' },
        friends: { title: 'Amici', description: 'Visualizza e gestisci le amicizie' },
        health: { title: 'Note sulla salute', description: 'Note private sulla salute e la memoria' },
        photos: { title: 'Foto', description: 'Carica foto di famiglia' },
        alerts: { title: 'Avvisi', description: 'Avvisi di protezione per il tuo caro' },
        social: {
          title: 'Social Media', description: 'Facebook, YouTube, feed RSS e aggiornamenti familiari',
          family_updates: 'Aggiornamenti famiglia',
          family_updates_desc: 'Condividi qualcosa successo in famiglia — Lena lo menzionerà in conversazione.',
          person_name_placeholder: 'Di chi si tratta? (es. Carla)',
          relationship_placeholder: 'Relazione (es. figlia, nipote)',
          what_happened_placeholder: 'Cosa è successo? Scrivi un aggiornamento...',
          share_update: 'Condividi aggiornamento',
          youtube_channels: 'Canali YouTube',
          youtube_desc: 'Aggiungi un canale YouTube — Lena può menzionare nuovi video.',
          channel_name_placeholder: 'Nome canale (es. canale di cucina di Carla)',
          channel_url_placeholder: 'URL o handle YouTube (es. @carla)',
          add_channel: 'Aggiungi canale',
          rss_advanced: 'Avanzato: feed RSS',
        },
        news: { title: 'Rassegna stampa', description: 'Notizie personalizzate per le conversazioni' },
        reminders: { title: 'Promemoria', description: 'Imposta promemoria e compleanni' },
        calls: { title: 'Chiamate proattive', description: 'Pianifica chiamate di controllo del compagno' },
        companion: { title: 'Impostazioni compagno', description: 'Configura aspetto, voce e comportamento del compagno' },
        personality: {
          title: 'Personalità',
          nurturer: 'Premuroso', straightTalker: 'Diretto', joker: 'Burlone', intellectual: 'Intellettuale', listener: 'Ascoltatore',
          warmth: 'Calore', humour: 'Umorismo', pace: 'Ritmo', formality: 'Formalità', proactivity: 'Proattività',
          warmthLeft: 'Caloroso e premuroso', warmthRight: 'Diretto',
          humourLeft: 'Giocoso e divertente', humourRight: 'Calmo e serio',
          paceLeft: 'Chiacchierone ed espansivo', paceRight: 'Conciso',
          formalityLeft: 'Informale e amichevole', formalityRight: 'Formale e rispettoso',
          proactivityLeft: 'Prende iniziativa', proactivityRight: "Segue l'utente",
          preview: 'Con queste impostazioni, {name} sarà:',
          custom: 'Personalizzato',
        },
        activity: { title: 'Attività conversazione', description: 'Riepiloghi recenti delle conversazioni' },
        wellbeing: {
          title: 'Benessere',
          description: 'Riepilogo del benessere emotivo dalle conversazioni',
          intro: 'Ecco come si è sentito {name} durante le conversazioni con {companion}',
          period: 'Periodo',
          mood: 'Umore',
          lastCall: 'Ultima chiamata',
          thisWeek: 'Questa settimana',
          lastWeek: 'Settimana scorsa',
          lastMonth: 'Ultimo mese',
          noCalls: 'Nessuna chiamata',
          noData: 'Nessun dato di conversazione ancora disponibile.',
          moods: { happy: 'Felice', neutral: 'Neutrale', low: 'Giù' },
          periodMoods: { happy: 'Per lo più positivo', neutral: 'Misto', low: 'Per lo più giù' },
          privacyNote: 'Basato solo sul tono della conversazione \u2014 nessun dettaglio viene condiviso',
        },
        gifts: {
          title: 'Idee regalo',
          description: 'Suggerimenti regalo personalizzati',
          refresh: 'Aggiorna suggerimenti',
          empty: 'Aggiungi interessi per ottenere idee regalo',
        },
        pets: { title: 'Animali domestici', description: 'Animali domestici e le storie attorno a loro' },
        faith: { title: 'Fede', description: 'Fede, tradizioni e pratica spirituale' },
        childhood: { title: 'Infanzia', description: 'Storie e ricordi dei primi anni' },
        work: { title: 'Vita lavorativa', description: 'Carriera e ciò di cui andavano fieri' },
        account: { title: 'Account', description: 'Impostazioni del tuo account familiare' },
      },
      tablet: {
        close_photos: 'Chiudi',
      },
      comingSoon: 'Prossimamente',
      detail: {
        backToDashboard: 'Torna alla dashboard',
        loading: 'Caricamento…',
        noIntakeTitle: 'Nessun colloquio completato',
        noIntakeText: 'Completa il colloquio con Mia per compilare questa sezione.',
        fetchError: 'Impossibile caricare i dati. Riprova.',
        name: 'Nome',
        nickname: 'Soprannome',
        age: 'Età',
        language: 'Lingua',
        location: 'Posizione',
        noInterests: 'Nessun interesse registrato.',
        noFamily: 'Nessun familiare registrato.',
        noFriends: 'Nessun amico aggiunto.',
        noHealth: 'Nessuna nota sulla salute registrata.',
        familyOnly: 'Visibile solo alla famiglia',
        addReminder: 'Aggiungi promemoria',
        reminderPlaceholder: 'es. Chiama Sophie, prendi le pillole...',
        birthdayName: 'Nome',
        birthdayDate: 'Data (MM-GG)',
        addBirthday: 'Aggiungi compleanno',
        pending: 'In sospeso',
        completed: 'Completato',
        noReminders: 'Nessun promemoria ancora.',
        dueDate: 'Scadenza',
        optional: 'opzionale',
      },
    },
    tablet: {
      callstrip: {
        status: {
          idle: '',
          wake_listening: 'Mi sveglio',
          listening: 'In ascolto',
          transcribing: 'Fammi pensare',
          thinking: 'Fammi pensare',
          speaking: 'Sto parlando',
          reconnecting: 'Riconnessione',
          error: 'Qualcosa è andato storto',
          quota: 'Riposo per ora',
          music_playing: 'Musica in riproduzione',
          // #687 — wait mode label (IT).
          waiting: 'In attesa',
        },
        endCall: 'Termina chiamata',
        hiccup: 'Piccolo intoppo',
        tryingAgain: 'Riprovo',
        mutedMicLabel: 'microfono disattivato',
        // #551
        youSaidLabel: 'Hai detto',
        hiccupTooltip: 'Piccolo intoppo di connessione — l\'ultimo turno non è arrivato pulito al server.',
        // #700 — pause/resume button labels (IT).
        pauseLabel: 'Un momento di silenzio',
        resumeLabel: 'Riprendi',
      },
      yousaid: {
        placeholderTitle: 'Dimmi pure qualcosa',
        placeholderBody: 'Sono qui quando sei pronto.',
        liveLabel: 'Ti ascolto…',
        frozenLabel: 'Hai detto',
      },
      bertareply: {
        label: 'Lena',
        idlePlaceholder: 'Ciao — sono qui quando hai voglia di chiacchierare.',
        you: 'Tu',  // #1032
      },
      photocarousel: {
        done: 'Fatto',
        prev: 'Foto precedente',
        next: 'Foto successiva',
        zoomIn: 'Ingrandisci',
        zoomOut: 'Riduci',
        help: 'Aiuto',
        closeAria: 'Chiudi foto',
        bertaSaying: 'Lena sta dicendo',
      },
      videooverlay: {
        done: 'Fatto',
        play: 'Riproduci',
        pause: 'Pausa',
        volumeDown: 'Abbassa volume',
        volumeUp: 'Alza volume',
        closeAria: 'Chiudi video',
      },
      musicview: {
        nowPlaying: 'In riproduzione',
        fallbackTitle: 'Musica',
        play: 'Riproduci',
        pause: 'Pausa',
        volumeDown: 'Abbassa volume',
        volumeUp: 'Alza volume',
        done: 'Fatto',
        closeAria: 'Chiudi lettore musicale',
      },
      dominogame: {
        done: 'Fatto',
        doneAria: 'Termina la partita a domino',
        knock: 'Passa (salta)',
        knockAria: 'Passa — salta il turno (solo se non hai una tessera giocabile)',
        thinking: '{name} sta pensando…',
        passed: '{name} ha passato',
        bertaStackLabel: '{name} — {count} tessere',
        userTilesLabel: 'LE TUE TESSERE — {count} tessere',
        userTilesYourTurn: ' • tocca a te',
        placeOnLeft: 'Posiziona tessera al lato sinistro della fila',
        placeOnRight: 'Posiziona tessera al lato destro della fila',
        // #551
        emptyChainHint: 'Tocca una tessera dalla tua mano e poi qui per iniziare',
        endTitleUser:    'Che bella partita!',
        endSubtitleUser: 'Hai giocato benissimo.',
        endTitleBerta:    'Che bella partita!',
        endSubtitleBerta: 'È stato un piacere giocare con te.',
        endTitleTie:    'Una partita perfettamente in equilibrio!',
        endSubtitleTie: 'Abbiamo giocato bene entrambi.',
      },
      solitairegame: {
        done: 'Fatto',
        doneAria: 'Termina il solitario',
        undo: 'Annulla',
        undoAria: 'Annulla l\'ultima mossa',
        stockAria: 'Pesca una carta dal mazzo',
        recycleHint: 'Tocca per girare il mazzo',
        emptyColumnHint: 'Solo un Re può aprire una colonna vuota',
        foundationAria: 'Base, {suit}',
        stuckTitle: 'Le carte non ci stanno aiutando',
        stuckBody: 'Niente più mosse questa volta — è solo la fortuna delle carte. Proviamo una nuova mano? Basta chiedere, oppure tocca Fatto.',
        winTitle: 'Che bella partita!',
        winBody: 'Ogni carta è al suo posto. Hai giocato benissimo.',
        instructions: 'Tocca una carta, poi dove deve andare. Tocca il mazzo per altre carte.',
      },
      gamecarousel: {
        header: 'A cosa giochiamo?',
        play: 'Gioca',
        position: 'Gioco {index} di {total}',
        favourite: 'Questo ti piace',
        closeAria: 'Chiudere la scelta dei giochi',
        prevAria: 'Gioco precedente',
        nextAria: 'Gioco successivo',
        titles: { solitaire: 'Solitario', domino: 'Domino' },
        descriptions: {
          solitaire: 'Una tranquilla partita a solitario con un mazzo di carte. Gioca con calma.',
          domino: "A turno, accostando le tessere da un capo all'altro. Rilassante e piacevole, senza punteggio da inseguire.",
        },
      },
      idledim: {
        nightWakeHint: 'Tieni premuto o tocca due volte per svegliare',
      },
      settings: {
        title: 'Impostazioni',
        sectionCompanionStyle: 'Stile del compagno',
        styleSmiley: '😊 Faccina',
        styleWaveform: '📊 Onda',
        styleTextOnly: '🔤 Solo testo',
        gearAria: 'Impostazioni',
        refreshAria: 'Aggiorna l’app (tieni premuto)',
        // #551
        sectionMicrophone: 'Microfono',
      },
      idle: {
        tapToCall: 'Tocca per chiamare',
        // #551
        callButton: '📞 Chiama {name}',
      },
      calling: {
        callingX: 'Sto chiamando {name}…',
        // #551
        callEnded: 'Chiamata terminata',
      },
      incoming: {
        callingFrom: 'Chiamata in arrivo da {name}',
        accept: 'Rispondi',
        decline: 'Rifiuta',
        // #551
        callingYou: '{name} ti sta chiamando',
        notNow: 'Non adesso',
      },
      hangup: {
        button: '📵 Riaggancia',
      },
      activity: {
        closeAria: 'Chiudi',
      },
      micwarning: {
        cantHearWell: 'Non ti sento molto bene — potresti parlare un po\' più forte?',
        // #544 — first-empty-turn reassurance, informal-tu register.
        takeYourTime: 'Tranquillo, prenditi pure il tuo tempo',
        // #544 — 3+-empty-turn mic-hardware warning.
        checkMicrophone: 'Faccio fatica a sentirti — controlla il microfono',
        // #551
        accessDenied: 'Serve l\'accesso al microfono. Consentilo nelle impostazioni del browser.',
        notFound: 'Nessun microfono trovato. Collega un microfono.',
        generic: 'Errore microfono — riprova',
      },
      mictest: {
        idle:      '🎤 Prova il microfono',
        recording: '⏹ Ferma e controlla',
        checking:  '⏳ Verifica in corso…',
        weHeard:   '✅ Abbiamo sentito: "{text}"',
        notHeard:  '❌ Non ti abbiamo sentito chiaramente',
      },
      selftest: {
        section:     '🎧 Autotest audio',
        record:      '🔴 Registra',
        recordAgain: '🔴 Registra di nuovo',
        stop:        '⏹ Ferma registrazione',
        play:        '▶️ Riproduci',
        playing:     '🔊 In riproduzione…',
        hint:        'Registra esattamente ciò che viene inviato al server e poi lo riproduce così puoi valutare la qualità catturata.',
        error:       '❌ Registrazione non riuscita — la cattura ad alta fedeltà (PCM) non è disponibile in questo browser.',
        raw:          '🎛 Microfono grezzo (senza soppressione del rumore / AGC)',
        rawHint:      'Off = esattamente ciò che inviano le chiamate. On = microfono grezzo, per confrontare il timbro / il calore.',
        tagRaw:       'grezzo',
        tagProcessed: 'come inviato',
      },
      domino: {
        moveError: 'Quella mossa non ha funzionato — prova con un\'altra tessera.',
      },
      setup: {
        activatingTitle: 'Sto configurando il tuo tablet…',
        activatingBody:  'Un attimo — stiamo collegando questo tablet al tuo account.',
        failedTitle:     'Configurazione non riuscita',
        failedBody:      'Torna al {strongOpen}portale familiare{strongClose} e scansiona un nuovo codice QR.',
        notSetUpTitle:   'Questo tablet non è ancora configurato',
        notSetUpBody:    'Chiedi a un familiare di aprire il portale Amigo Mio e scansionare il codice QR con questo tablet.',
        confirmTitle:     'Configurare questo tablet?',
        confirmBody:      'Questo tablet verrà configurato per {name}, con {companion} come compagna.',
        confirmBodyNoName:'Questo tablet verrà configurato per la persona a cui vuoi bene.',
        confirmButton:    'Configura questo tablet',
        confirmCancel:    'Non ora',
      },
    },
  },

  de: {
    hero: {
      headline: 'Sie spricht jeden Morgen mit Lena.',
      subline:
        'Amigo Mio gibt Ihrem Angehörigen einen Begleiter, der wirklich zuhört — und niemals vergisst.',
      cta: 'Profil erstellen',
    },
    selfWedge: {
      headline: 'Möchten Sie es für sich selbst einrichten?',
      subline:
        'Ja, das geht auch. Erstellen Sie Ihr eigenes Konto; die Fragen passen sich an.',
      cta: 'Mein eigenes Konto erstellen',
    },
    values: [
      {
        icon: '🎙️',
        heading: 'Spricht über alles',
        description:
          'Fußball, Erinnerungen, Nachrichten, ein schwerer Tag. Amigo Mio urteilt nie und wechselt nie das Thema.',
      },
      {
        icon: '🧠',
        heading: 'Erinnert sich an das Wichtige',
        description:
          'Jeden Namen, jede Geschichte, jede Vorliebe. Ihr Angehöriger muss sich nie wiederholen.',
      },
      {
        icon: '👨‍👩‍👧',
        heading: 'Hält die Familie nah',
        description:
          'Sie sehen, wie es ihnen geht — ohne je in ihre privaten Momente einzudringen.',
      },
    ],
    nonprofit: {
      badge: 'Non-profit',
      text: 'Amigo Mio ist ein gemeinnütziges Projekt. Wir halten die Kosten so niedrig wie möglich — und der erste Monat ist komplett kostenlos.',
      trial: '1 Monat kostenlos · Keine Kreditkarte erforderlich',
    },
    nav: {
      signIn: 'Anmelden',
      createProfile: 'Profil erstellen',
    },
    footer: {
      alreadyHaveAccount: 'Haben Sie bereits ein Konto?',
      signIn: 'Anmelden',
    },
    relations: {
      aunt: 'Tante',
      boyfriend: 'Freund',
      brother: 'Bruder',
      caregiver: 'Betreuer',
      daughter: 'Tochter',
      ex: 'Ex',
      father: 'Vater',
      girlfriend: 'Freundin',
      grandchild: 'Enkelkind',
      mother: 'Mutter',
      nephew: 'Neffe',
      niece: 'Nichte',
      parent: 'Elternteil',
      partner: 'Partner',
      sibling: 'Geschwister',
      sister: 'Schwester',
      son: 'Sohn',
      spouse: 'Ehepartner',
      uncle: 'Onkel',
      'family member': 'Anderes Familienmitglied',
    },
    signup: {
      title: 'Profil erstellen',
      relationLabel: 'Ich bin der/die … der älteren Person',
      relationPlaceholder: '— auswählen —',
      change: '← Ändern',
      terms: { agree: 'Ich stimme den', link: 'AmigoMio-Bedingungen zu', read: '(lesen)', hide: '(ausblenden)', required: 'Bitte akzeptiere die AmigoMio-Bedingungen, um ein Konto zu erstellen.' },
      name: 'Vollständiger Name',
      email: 'E-Mail-Adresse',
      password: 'Passwort',
      confirmPassword: 'Passwort bestätigen',
      submit: 'Konto erstellen',
      haveAccount: 'Haben Sie bereits ein Konto?',
      signIn: 'Anmelden',
      choice: {
        title: 'Für wen ist das?',
        subtitle: 'Wählen Sie das, was zu Ihnen passt. Auf dem nächsten Bildschirm können Sie noch wechseln.',
        forLoved: '👵  Für einen Angehörigen',
        forSelf: '🧓  Für mich selbst',
      },
      errors: {
        passwordMismatch: 'Passwörter stimmen nicht überein.',
        generic: 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.',
        emailExists: 'Ein Konto mit dieser E-Mail-Adresse existiert bereits.',
      },
    },
    login: {
      title: 'Anmelden',
      email: 'E-Mail-Adresse',
      password: 'Passwort',
      submit: 'Anmelden',
      noAccount: 'Noch kein Konto?',
      signUp: 'Registrieren',
      errors: {
        invalid: 'Ungültige E-Mail oder ungültiges Passwort.',
        generic: 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.',
      },
    },
    intake: {
      title: 'Lerne Mia kennen',
      subtitle: 'Ich helfe dir, ein Profil für deinen Angehörigen zu erstellen. Es dauert etwa 5 Minuten — du kannst sprechen oder tippen.',
      talkWithMia: 'Mit Mia sprechen',
      chatWithMia: 'Mit Mia chatten',
      question: 'Frage',
      of: 'von',
      switchToVoice: 'Zur Spracheingabe wechseln',
      switchToText: 'Zur Texteingabe wechseln',
      inputPlaceholder: 'Gib deine Antwort ein…',
      tapToSpeak: 'Tippen zum Sprechen',
      tapToSend: 'Tippen zum Senden',
      voiceConnecting: 'Verbinde mit Mia…',
      voiceReady: 'Sprich einfach — ich höre zu.',
      voiceListening: 'Mia spricht…',
      voiceRecording: 'Höre zu…',
      voiceProcessing: 'Wird verarbeitet…',
      voiceDone: 'Alles erledigt! Du wirst zu deinem Dashboard weitergeleitet…',
      voiceError: 'Verbindungsfehler. Bitte Seite neu laden.',
      voiceMicError: 'Mikrofonzugriff verweigert.',
      errorStart: 'Etwas ist schiefgelaufen. Bitte neu laden und erneut versuchen.',
      errorGeneric: 'Etwas ist schiefgelaufen. Bitte erneut versuchen.',
      callPrompt: 'Wenn du bereit bist, drück auf Start.',
      connecting: 'Verbinde…',
      miaSpeaking: 'Mia spricht…',
      connectError: 'Verbindung fehlgeschlagen. Bitte Seite neu laden.',
      startBtn: 'Start',
      startCall: 'Start',
      endCall: 'Gespräch beenden',
      startOver: 'Von vorne beginnen',
    },
    portal: {
      welcome: 'Willkommen',
      logout: 'Abmelden',
      startIntake: 'Intake mit Mia starten',
      sections: {
        profile: { title: 'Profil', description: 'Profil Ihres Angehörigen anzeigen und bearbeiten' },
        interests: { title: 'Interessen & Themen', description: 'Interessen und gesperrte Themen verwalten' },
        family: { title: 'Familienmitglieder', description: 'Familienverbindungen hinzufügen und verwalten' },
        friends: { title: 'Freunde', description: 'Freundschaften anzeigen und verwalten' },
        health: { title: 'Gesundheitsnotizen', description: 'Private Gesundheits- und Gedächtnisnotizen' },
        photos: { title: 'Fotos', description: 'Familienfotos hochladen' },
        alerts: { title: 'Warnmeldungen', description: 'Schutzwarnungen für Ihren Angehörigen' },
        social: {
          title: 'Soziale Medien', description: 'Facebook, YouTube, RSS-Feeds und Familien-Updates',
          family_updates: 'Familien-Updates',
          family_updates_desc: 'Teile etwas, was in der Familie passiert ist — Lena wird es im Gespräch erwähnen.',
          person_name_placeholder: 'Um wen geht es? (z.B. Carla)',
          relationship_placeholder: 'Beziehung (z.B. Tochter, Enkel)',
          what_happened_placeholder: 'Was ist passiert? Schreibe ein Update...',
          share_update: 'Update teilen',
          youtube_channels: 'YouTube-Kanäle',
          youtube_desc: 'Füge einen YouTube-Kanal hinzu — Lena kann neue Videos erwähnen.',
          channel_name_placeholder: 'Kanalname (z.B. Carlas Kochkanal)',
          channel_url_placeholder: 'YouTube-URL oder Handle (z.B. @carla)',
          add_channel: 'Kanal hinzufügen',
          rss_advanced: 'Erweitert: RSS-Feed',
        },
        news: { title: 'Nachrichtenbriefing', description: 'Interessenbasierte Nachrichten für Gespräche' },
        reminders: { title: 'Erinnerungen', description: 'Erinnerungen und Geburtstage einrichten' },
        calls: { title: 'Proaktive Anrufe', description: 'Begleiter-Check-in-Anrufe planen' },
        companion: { title: 'Begleiter-Einstellungen', description: 'Aussehen, Stimme und Verhalten des Begleiters konfigurieren' },
        personality: {
          title: 'Persönlichkeit',
          nurturer: 'Fürsorglich', straightTalker: 'Geradeheraus', joker: 'Spaßvogel', intellectual: 'Intellektuell', listener: 'Zuhörer',
          warmth: 'Wärme', humour: 'Humor', pace: 'Tempo', formality: 'Formalität', proactivity: 'Proaktivität',
          warmthLeft: 'Warm & fürsorglich', warmthRight: 'Direkt',
          humourLeft: 'Verspielt & lustig', humourRight: 'Ruhig & ernst',
          paceLeft: 'Gesprächig & ausführlich', paceRight: 'Knapp',
          formalityLeft: 'Ungezwungen & freundlich', formalityRight: 'Formell & respektvoll',
          proactivityLeft: 'Ergreift Initiative', proactivityRight: 'Folgt dem Benutzer',
          preview: 'Mit diesen Einstellungen wird {name} sein:',
          custom: 'Benutzerdefiniert',
        },
        activity: { title: 'Gesprächsaktivität', description: 'Aktuelle Gesprächszusammenfassungen' },
        wellbeing: {
          title: 'Wohlbefinden',
          description: 'Emotionale Zusammenfassung aus Gesprächen',
          intro: 'So hat sich {name} während der Gespräche mit {companion} gefühlt',
          period: 'Zeitraum',
          mood: 'Stimmung',
          lastCall: 'Letztes Gespräch',
          thisWeek: 'Diese Woche',
          lastWeek: 'Letzte Woche',
          lastMonth: 'Letzter Monat',
          noCalls: 'Keine Gespräche',
          noData: 'Noch keine Gesprächsdaten verfügbar.',
          moods: { happy: 'Fröhlich', neutral: 'Neutral', low: 'Niedergeschlagen' },
          periodMoods: { happy: 'Überwiegend positiv', neutral: 'Gemischt', low: 'Überwiegend niedergeschlagen' },
          privacyNote: 'Basierend nur auf dem Gesprächston \u2014 es werden keine Details geteilt',
        },
        gifts: {
          title: 'Geschenkideen',
          description: 'Personalisierte Geschenkvorschl\u00e4ge',
          refresh: 'Vorschl\u00e4ge aktualisieren',
          empty: 'F\u00fcgen Sie Interessen hinzu, um Geschenkideen zu erhalten',
        },
        pets: { title: 'Haustiere', description: 'Haustiere und die Geschichten rund um sie' },
        faith: { title: 'Glaube', description: 'Glaube, Traditionen und spirituelle Praxis' },
        childhood: { title: 'Kindheit', description: 'Geschichten und Erinnerungen aus den fr\u00fchen Jahren' },
        work: { title: 'Berufsleben', description: 'Karriere und worauf sie stolz waren' },
        account: { title: 'Konto', description: 'Einstellungen f\u00fcr dein Familienkonto' },
      },
      tablet: {
        close_photos: 'Schließen',
      },
      comingSoon: 'Demnächst verfügbar',
      detail: {
        backToDashboard: 'Zurück zum Dashboard',
        loading: 'Wird geladen…',
        noIntakeTitle: 'Noch kein Intake abgeschlossen',
        noIntakeText: 'Schließen Sie das Intake mit Mia ab, um diesen Bereich auszufüllen.',
        fetchError: 'Daten konnten nicht geladen werden. Bitte versuchen Sie es erneut.',
        name: 'Name',
        nickname: 'Spitzname',
        age: 'Alter',
        language: 'Sprache',
        location: 'Standort',
        noInterests: 'Noch keine Interessen erfasst.',
        noFamily: 'Noch keine Familienmitglieder erfasst.',
        noFriends: 'Noch keine Freunde hinzugefügt.',
        noHealth: 'Keine Gesundheitsnotizen erfasst.',
        familyOnly: 'Nur für die Familie sichtbar',
        addReminder: 'Erinnerung hinzufügen',
        reminderPlaceholder: 'z.B. Sophie anrufen, Pillen nehmen...',
        birthdayName: 'Name',
        birthdayDate: 'Datum (MM-TT)',
        addBirthday: 'Geburtstag hinzufügen',
        pending: 'Ausstehend',
        completed: 'Erledigt',
        noReminders: 'Noch keine Erinnerungen.',
        dueDate: 'Fälligkeitsdatum',
        optional: 'optional',
      },
    },
    tablet: {
      callstrip: {
        status: {
          idle: '',
          wake_listening: 'Wache auf',
          listening: 'Höre zu',
          transcribing: 'Lassen Sie mich überlegen',
          thinking: 'Lassen Sie mich überlegen',
          speaking: 'Spreche',
          reconnecting: 'Verbinde neu',
          error: 'Etwas ist schiefgelaufen',
          quota: 'Ich pausiere kurz',
          music_playing: 'Musik läuft',
          // #687 — wait mode label (DE).
          waiting: 'Wartet',
        },
        endCall: 'Gespräch beenden',
        hiccup: 'Kleiner Schluckauf',
        tryingAgain: 'Versuche es erneut',
        mutedMicLabel: 'Mikrofon stummgeschaltet',
        // #551
        youSaidLabel: 'Du sagtest',
        hiccupTooltip: 'Verbindungsaussetzer — der letzte Zug kam nicht sauber beim Server an.',
        // #700 — pause/resume button labels (DE).
        pauseLabel: 'Einen Moment leise',
        resumeLabel: 'Weitermachen',
      },
      yousaid: {
        placeholderTitle: 'Sagen Sie ruhig etwas',
        placeholderBody: 'Ich bin hier, wann immer Sie bereit sind.',
        liveLabel: 'Ich höre Sie…',
        frozenLabel: 'Sie sagten',
      },
      bertareply: {
        label: 'Lena',
        idlePlaceholder: 'Hallo — ich bin hier, wann immer Sie plaudern möchten.',
        you: 'Du',  // #1032
      },
      photocarousel: {
        done: 'Fertig',
        prev: 'Vorheriges Foto',
        next: 'Nächstes Foto',
        zoomIn: 'Vergrößern',
        zoomOut: 'Verkleinern',
        help: 'Hilfe',
        closeAria: 'Fotos schließen',
        bertaSaying: 'Lena sagt',
      },
      videooverlay: {
        done: 'Fertig',
        play: 'Abspielen',
        pause: 'Pause',
        volumeDown: 'Leiser',
        volumeUp: 'Lauter',
        closeAria: 'Video schließen',
      },
      musicview: {
        nowPlaying: 'Läuft gerade',
        fallbackTitle: 'Musik',
        play: 'Abspielen',
        pause: 'Pause',
        volumeDown: 'Leiser',
        volumeUp: 'Lauter',
        done: 'Fertig',
        closeAria: 'Musikplayer schließen',
      },
      dominogame: {
        done: 'Fertig',
        doneAria: 'Domino-Spiel beenden',
        knock: 'Klopfen (überspringen)',
        knockAria: 'Klopfen — Zug überspringen (nur wenn Sie keinen spielbaren Stein haben)',
        thinking: '{name} überlegt…',
        passed: '{name} hat gepasst',
        bertaStackLabel: '{name} — {count} Steine',
        userTilesLabel: 'IHRE STEINE — {count} Steine',
        userTilesYourTurn: ' • Sie sind dran',
        placeOnLeft: 'Stein am linken Ende der Reihe anlegen',
        placeOnRight: 'Stein am rechten Ende der Reihe anlegen',
        // #551
        emptyChainHint: 'Tippe einen Stein aus deiner Hand und dann hier, um anzufangen',
        endTitleUser:    'Was für ein schönes Spiel!',
        endSubtitleUser: 'Du hast wunderbar gespielt.',
        endTitleBerta:    'Was für ein schönes Spiel!',
        endSubtitleBerta: 'Es war schön, mit dir zu spielen.',
        endTitleTie:    'Ein perfekt ausgeglichenes Spiel!',
        endSubtitleTie: 'Wir haben beide gut gespielt.',
      },
      solitairegame: {
        done: 'Fertig',
        doneAria: 'Solitär beenden',
        undo: 'Rückgängig',
        undoAria: 'Den letzten Zug rückgängig machen',
        stockAria: 'Eine Karte vom Stapel ziehen',
        recycleHint: 'Tippen, um den Stapel umzudrehen',
        emptyColumnHint: 'Nur ein König darf eine leere Spalte beginnen',
        foundationAria: 'Ablage, {suit}',
        stuckTitle: 'Die Karten meinen es heute nicht gut mit uns',
        stuckBody: 'Diesmal geht kein Zug mehr — das ist nur das Glück der Karten. Sollen wir neu geben? Sag einfach Bescheid oder tippe auf Fertig.',
        winTitle: 'Was für ein schönes Spiel!',
        winBody: 'Jede Karte ist zu Hause. Wunderbar gespielt.',
        instructions: 'Tippe eine Karte an und dann, wohin sie soll. Tippe den Stapel für mehr Karten.',
      },
      gamecarousel: {
        header: 'Was sollen wir spielen?',
        play: 'Spielen',
        position: 'Spiel {index} von {total}',
        favourite: 'Das magst du',
        closeAria: 'Die Spielauswahl schließen',
        prevAria: 'Vorheriges Spiel',
        nextAria: 'Nächstes Spiel',
        titles: { solitaire: 'Solitär', domino: 'Domino' },
        descriptions: {
          solitaire: 'Eine ruhige Partie Solitär mit einem Kartenspiel. Spiel ganz in deinem Tempo.',
          domino: 'Abwechselnd Steine aneinanderlegen. Entspannt und gesellig — kein Punktejagen.',
        },
      },
      idledim: {
        nightWakeHint: 'Lange drücken oder doppelt tippen zum Aufwecken',
      },
      settings: {
        title: 'Einstellungen',
        sectionCompanionStyle: 'Begleiter-Stil',
        styleSmiley: '😊 Smiley',
        styleWaveform: '📊 Wellenform',
        styleTextOnly: '🔤 Nur Text',
        gearAria: 'Einstellungen',
        refreshAria: 'App aktualisieren (lange drücken)',
        // #551
        sectionMicrophone: 'Mikrofon',
      },
      idle: {
        tapToCall: 'Tippen zum Anrufen',
        // #551
        callButton: '📞 {name} anrufen',
      },
      calling: {
        callingX: 'Rufe {name} an…',
        // #551
        callEnded: 'Gespräch beendet',
      },
      incoming: {
        callingFrom: 'Eingehender Anruf von {name}',
        accept: 'Annehmen',
        decline: 'Ablehnen',
        // #551
        callingYou: '{name} ruft dich an',
        notNow: 'Jetzt nicht',
      },
      hangup: {
        button: '📵 Auflegen',
      },
      activity: {
        closeAria: 'Schließen',
      },
      micwarning: {
        cantHearWell: 'Ich kann Sie nicht so gut hören — könnten Sie etwas lauter sprechen?',
        // #544 — first-empty-turn reassurance, informal-du register
        // matching the rest of the German tablet copy.
        takeYourTime: 'Keine Sorge, lass dir ruhig Zeit',
        // #544 — 3+-empty-turn mic-hardware warning.
        checkMicrophone: 'Ich kann dich schlecht hören — schau bitte mal das Mikrofon an',
        // #551
        accessDenied: 'Mikrofonzugriff erforderlich. Bitte erlaube ihn in den Browser-Einstellungen.',
        notFound: 'Kein Mikrofon gefunden. Bitte schließe ein Mikrofon an.',
        generic: 'Mikrofon-Fehler — bitte erneut versuchen',
      },
      mictest: {
        idle:      '🎤 Mikrofon testen',
        recording: '⏹ Stoppen und prüfen',
        checking:  '⏳ Wird geprüft…',
        weHeard:   '✅ Wir haben gehört: „{text}"',
        notHeard:  '❌ Wir konnten dich nicht klar verstehen',
      },
      selftest: {
        section:     '🎧 Audio-Selbsttest',
        record:      '🔴 Aufnehmen',
        recordAgain: '🔴 Erneut aufnehmen',
        stop:        '⏹ Aufnahme stoppen',
        play:        '▶️ Abspielen',
        playing:     '🔊 Wird abgespielt…',
        hint:        'Nimmt genau das auf, was an den Server gesendet wird, und spielt es danach ab, damit du die aufgenommene Qualität beurteilen kannst.',
        error:       '❌ Aufnahme fehlgeschlagen — hochauflösende Aufnahme (PCM) ist in diesem Browser nicht verfügbar.',
        raw:          '🎛 Rohes Mikrofon (ohne Rauschunterdrückung / AGC)',
        rawHint:      'Aus = genau das, was Anrufe senden. Ein = unbearbeitetes Mikrofon, zum Vergleich von Klang / Wärme.',
        tagRaw:       'roh',
        tagProcessed: 'wie gesendet',
      },
      domino: {
        moveError: 'Dieser Zug ging nicht — versuche einen anderen Stein.',
      },
      setup: {
        activatingTitle: 'Tablet wird eingerichtet…',
        activatingBody:  'Einen Moment — wir verbinden dieses Tablet mit deinem Konto.',
        failedTitle:     'Einrichtung fehlgeschlagen',
        failedBody:      'Geh zurück zum {strongOpen}Familienportal{strongClose} und scanne einen neuen QR-Code.',
        notSetUpTitle:   'Dieses Tablet ist noch nicht eingerichtet',
        notSetUpBody:    'Bitte ein Familienmitglied, das Amigo-Mio-Portal zu öffnen und den QR-Code mit diesem Tablet zu scannen.',
        confirmTitle:     'Dieses Tablet einrichten?',
        confirmBody:      'Dieses Tablet wird für {name} eingerichtet, mit {companion} als Begleiterin.',
        confirmBodyNoName:'Dieses Tablet wird für Ihren Angehörigen eingerichtet.',
        confirmButton:    'Dieses Tablet einrichten',
        confirmCancel:    'Jetzt nicht',
      },
    },
  },

  fr: {
    hero: {
      headline: 'Elle parle avec Lena chaque matin.',
      subline:
        'Amigo Mio offre à votre proche un compagnon qui écoute vraiment — et qui n\'oublie jamais.',
      cta: 'Créer un profil',
    },
    selfWedge: {
      headline: 'Vous l\'installez pour vous-même ?',
      subline:
        'Oui, c\'est aussi possible. Créez votre propre compte ; les questions s\'adaptent.',
      cta: 'Créer mon propre compte',
    },
    values: [
      {
        icon: '🎙️',
        heading: 'Parle de tout',
        description:
          'Le foot, les souvenirs, l\'actualité, une journée difficile. Amigo Mio ne juge jamais et ne change jamais de sujet.',
      },
      {
        icon: '🧠',
        heading: 'Se souvient de l\'essentiel',
        description:
          'Chaque prénom, chaque histoire, chaque préférence. Votre proche n\'aura plus jamais à se répéter.',
      },
      {
        icon: '👨‍👩‍👧',
        heading: 'Garde la famille proche',
        description:
          'Vous verrez comment ils vont — sans jamais empiéter sur leurs moments privés.',
      },
    ],
    nonprofit: {
      badge: 'Association',
      text: 'Amigo Mio est un projet à but non lucratif. Nous gardons les coûts aussi bas que possible — et le premier mois est entièrement gratuit.',
      trial: '1 mois gratuit · Aucune carte bancaire nécessaire',
    },
    nav: {
      signIn: 'Se connecter',
      createProfile: 'Créer un profil',
    },
    footer: {
      alreadyHaveAccount: 'Vous avez déjà un compte ?',
      signIn: 'Se connecter',
    },
    relations: {
      aunt: 'Tante',
      boyfriend: 'Petit ami',
      brother: 'Frère',
      caregiver: 'Aidant',
      daughter: 'Fille',
      ex: 'Ex',
      father: 'Père',
      girlfriend: 'Petite amie',
      grandchild: 'Petit-enfant',
      mother: 'Mère',
      nephew: 'Neveu',
      niece: 'Nièce',
      parent: 'Parent',
      partner: 'Partenaire',
      sibling: 'Frère ou sœur',
      sister: 'Sœur',
      son: 'Fils',
      spouse: 'Conjoint(e)',
      uncle: 'Oncle',
      'family member': 'Autre membre de la famille',
    },
    signup: {
      title: 'Créer un profil',
      relationLabel: 'Je suis le/la … de la personne âgée',
      relationPlaceholder: '— sélectionner —',
      change: '← Changer',
      terms: { agree: "J'accepte les", link: 'conditions AmigoMio', read: '(lire)', hide: '(masquer)', required: 'Veuillez accepter les conditions AmigoMio pour créer un compte.' },
      name: 'Nom complet',
      email: 'Adresse e-mail',
      password: 'Mot de passe',
      confirmPassword: 'Confirmer le mot de passe',
      submit: 'Créer un compte',
      haveAccount: 'Vous avez déjà un compte ?',
      signIn: 'Se connecter',
      choice: {
        title: 'Pour qui est-ce ?',
        subtitle: 'Choisissez celui qui vous correspond. Vous pouvez changer d\'avis sur l\'écran suivant.',
        forLoved: '👵  Pour un proche',
        forSelf: '🧓  Pour moi-même',
      },
      errors: {
        passwordMismatch: 'Les mots de passe ne correspondent pas.',
        generic: 'Une erreur est survenue. Veuillez réessayer.',
        emailExists: 'Un compte existe déjà avec cette adresse e-mail.',
      },
    },
    login: {
      title: 'Se connecter',
      email: 'Adresse e-mail',
      password: 'Mot de passe',
      submit: 'Se connecter',
      noAccount: 'Pas encore de compte ?',
      signUp: 'S\'inscrire',
      errors: {
        invalid: 'E-mail ou mot de passe invalide.',
        generic: 'Une erreur est survenue. Veuillez réessayer.',
      },
    },
    intake: {
      title: 'Rencontre Mia',
      subtitle: 'Je vais t\'aider à créer un profil pour ton proche. Cela prend environ 5 minutes — tu peux parler ou écrire.',
      talkWithMia: 'Parler avec Mia',
      chatWithMia: 'Discuter avec Mia',
      question: 'Question',
      of: 'sur',
      switchToVoice: 'Passer à la voix',
      switchToText: 'Passer au texte',
      inputPlaceholder: 'Écris ta réponse…',
      tapToSpeak: 'Touche pour parler',
      tapToSend: 'Touche pour envoyer',
      voiceConnecting: 'Connexion à Mia…',
      voiceReady: 'Parle — je t\'écoute.',
      voiceListening: 'Mia parle…',
      voiceRecording: 'À l\'écoute…',
      voiceProcessing: 'Traitement en cours…',
      voiceDone: 'C\'est tout bon ! Direction ton tableau de bord…',
      voiceError: 'Erreur de connexion. Veuillez actualiser la page.',
      voiceMicError: 'Accès au microphone refusé.',
      errorStart: 'Une erreur est survenue. Actualise et réessaie.',
      errorGeneric: 'Une erreur est survenue. Veuillez réessayer.',
      callPrompt: 'Quand tu es prêt, appuie sur Démarrer.',
      connecting: 'Connexion…',
      miaSpeaking: 'Mia parle…',
      connectError: 'Connexion impossible. Actualise la page et réessaie.',
      startBtn: 'Démarrer',
      startCall: 'Démarrer',
      endCall: 'Terminer l\'appel',
      startOver: 'Recommencer',
    },
    portal: {
      welcome: 'Bienvenue',
      logout: 'Se déconnecter',
      startIntake: 'Démarrer l\'entretien avec Mia',
      sections: {
        profile: { title: 'Profil', description: 'Voir et modifier le profil de votre proche' },
        interests: { title: 'Centres d\'intérêt et sujets', description: 'Gérer les centres d\'intérêt et les sujets bloqués' },
        family: { title: 'Membres de la famille', description: 'Ajouter et gérer les liens familiaux' },
        friends: { title: 'Amis', description: 'Voir et gérer les amitiés' },
        health: { title: 'Notes de santé', description: 'Notes privées sur la santé et la mémoire' },
        photos: { title: 'Photos', description: 'Téléverser des photos de famille' },
        alerts: { title: 'Alertes', description: 'Alertes de protection pour votre proche' },
        social: {
          title: 'Réseaux sociaux', description: 'Facebook, YouTube, flux RSS et nouvelles familiales',
          family_updates: 'Nouvelles familiales',
          family_updates_desc: 'Partagez quelque chose qui s\'est passé en famille — Lena en parlera dans la conversation.',
          person_name_placeholder: 'De qui s\'agit-il ? (par ex. Carla)',
          relationship_placeholder: 'Lien (par ex. fille, petit-fils)',
          what_happened_placeholder: 'Que s\'est-il passé ? Écrivez une nouvelle...',
          share_update: 'Partager',
          youtube_channels: 'Chaînes YouTube',
          youtube_desc: 'Ajoutez une chaîne YouTube — Lena peut mentionner les nouvelles vidéos.',
          channel_name_placeholder: 'Nom de la chaîne (par ex. la chaîne cuisine de Carla)',
          channel_url_placeholder: 'URL ou identifiant YouTube (par ex. @carla)',
          add_channel: 'Ajouter une chaîne',
          rss_advanced: 'Avancé : flux RSS',
        },
        news: { title: 'Brief d\'actualités', description: 'Actualités personnalisées pour les conversations' },
        reminders: { title: 'Rappels', description: 'Définir des rappels et des anniversaires' },
        calls: { title: 'Appels proactifs', description: 'Programmer des appels de suivi du compagnon' },
        companion: { title: 'Réglages du compagnon', description: 'Configurer l\'apparence, la voix et le comportement du compagnon' },
        personality: {
          title: 'Personnalité',
          nurturer: 'Bienveillant', straightTalker: 'Franc', joker: 'Farceur', intellectual: 'Intellectuel', listener: 'Écoutant',
          warmth: 'Chaleur', humour: 'Humour', pace: 'Rythme', formality: 'Formalité', proactivity: 'Proactivité',
          warmthLeft: 'Chaleureux et bienveillant', warmthRight: 'Direct',
          humourLeft: 'Espiègle et drôle', humourRight: 'Calme et sérieux',
          paceLeft: 'Bavard et expansif', paceRight: 'Concis',
          formalityLeft: 'Décontracté et amical', formalityRight: 'Formel et respectueux',
          proactivityLeft: 'Prend l\'initiative', proactivityRight: 'Suit l\'utilisateur',
          preview: 'Avec ces réglages, {name} sera :',
          custom: 'Personnalisé',
        },
        activity: { title: 'Activité de conversation', description: 'Résumés récents des conversations' },
        wellbeing: {
          title: 'Bien-être',
          description: 'Résumé du bien-être émotionnel à partir des conversations',
          intro: 'Voici comment {name} s\'est senti pendant les conversations avec {companion}',
          period: 'Période',
          mood: 'Humeur',
          lastCall: 'Dernier appel',
          thisWeek: 'Cette semaine',
          lastWeek: 'Semaine dernière',
          lastMonth: 'Mois dernier',
          noCalls: 'Aucun appel',
          noData: 'Aucune donnée de conversation disponible pour le moment.',
          moods: { happy: 'Heureux', neutral: 'Neutre', low: 'Bas' },
          periodMoods: { happy: 'Plutôt positif', neutral: 'Mitigé', low: 'Plutôt bas' },
          privacyNote: 'Basé uniquement sur le ton de la conversation — aucun détail n\'est partagé',
        },
        gifts: {
          title: 'Idées cadeaux',
          description: 'Suggestions de cadeaux personnalisées',
          refresh: 'Actualiser les suggestions',
          empty: 'Ajoutez des centres d\'intérêt pour obtenir des idées cadeaux',
        },
        pets: { title: 'Animaux', description: 'Les animaux et les histoires autour d\'eux' },
        faith: { title: 'Foi', description: 'Foi, traditions et pratique spirituelle' },
        childhood: { title: 'Enfance', description: 'Récits et souvenirs des premières années' },
        work: { title: 'Vie professionnelle', description: 'Carrière et ce dont ils étaient fiers' },
        account: { title: 'Compte', description: 'Paramètres de votre compte familial' },
      },
      tablet: {
        close_photos: 'Fermer',
      },
      comingSoon: 'Bientôt disponible',
      detail: {
        backToDashboard: 'Retour au tableau de bord',
        loading: 'Chargement…',
        noIntakeTitle: 'Entretien pas encore terminé',
        noIntakeText: 'Terminez l\'entretien avec Mia pour remplir cette section.',
        fetchError: 'Impossible de charger les données. Veuillez réessayer.',
        name: 'Nom',
        nickname: 'Surnom',
        age: 'Âge',
        language: 'Langue',
        location: 'Lieu',
        noInterests: 'Aucun centre d\'intérêt enregistré.',
        noFamily: 'Aucun membre de la famille enregistré.',
        noFriends: 'Aucun ami ajouté.',
        noHealth: 'Aucune note de santé enregistrée.',
        familyOnly: 'Visible uniquement par la famille',
        addReminder: 'Ajouter un rappel',
        reminderPlaceholder: 'par ex. Appeler Sophie, prendre des médicaments...',
        birthdayName: 'Nom',
        birthdayDate: 'Date (MM-JJ)',
        addBirthday: 'Ajouter un anniversaire',
        pending: 'En attente',
        completed: 'Terminé',
        noReminders: 'Aucun rappel pour le moment.',
        dueDate: 'Échéance',
        optional: 'optionnel',
      },
    },
    tablet: {
      callstrip: {
        status: {
          idle: '',
          wake_listening: 'Je me réveille',
          listening: 'À l\'écoute',
          transcribing: 'Laisse-moi réfléchir',
          thinking: 'Laisse-moi réfléchir',
          speaking: 'Je parle',
          reconnecting: 'Reconnexion',
          error: 'Une erreur est survenue',
          quota: 'Je me repose un peu',
          music_playing: 'Musique en cours',
          // #687 — wait mode label (FR).
          waiting: 'En attente',
        },
        endCall: 'Terminer l\'appel',
        hiccup: 'Petit hoquet',
        tryingAgain: 'Je réessaie',
        mutedMicLabel: 'microphone coupé',
        // #551
        youSaidLabel: 'Tu as dit',
        hiccupTooltip: 'Petit hoquet de connexion — le dernier tour n\'est pas arrivé proprement au serveur.',
        // #700 — pause/resume button labels (FR).
        pauseLabel: 'Silence un instant',
        resumeLabel: 'Reprendre',
      },
      yousaid: {
        placeholderTitle: 'Dis quelque chose',
        placeholderBody: 'Je suis là quand tu es prêt.',
        liveLabel: 'Je t\'écoute…',
        frozenLabel: 'Tu as dit',
      },
      bertareply: {
        label: 'Lena',
        idlePlaceholder: 'Bonjour — je suis là quand tu veux discuter.',
        you: 'Toi',  // #1032
      },
      photocarousel: {
        done: 'Terminé',
        prev: 'Photo précédente',
        next: 'Photo suivante',
        zoomIn: 'Zoom avant',
        zoomOut: 'Zoom arrière',
        help: 'Aide',
        closeAria: 'Fermer les photos',
        bertaSaying: 'Lena dit',
      },
      videooverlay: {
        done: 'Terminé',
        play: 'Lecture',
        pause: 'Pause',
        volumeDown: 'Baisser le volume',
        volumeUp: 'Monter le volume',
        closeAria: 'Fermer la vidéo',
      },
      musicview: {
        nowPlaying: 'En cours de lecture',
        fallbackTitle: 'Musique',
        play: 'Lecture',
        pause: 'Pause',
        volumeDown: 'Baisser le volume',
        volumeUp: 'Monter le volume',
        done: 'Terminé',
        closeAria: 'Fermer le lecteur de musique',
      },
      dominogame: {
        done: 'Terminé',
        doneAria: 'Terminer la partie de dominos',
        knock: 'Passer (sauter)',
        knockAria: 'Passer — sauter le tour (uniquement quand tu n\'as aucun domino jouable)',
        thinking: '{name} réfléchit…',
        passed: '{name} a passé',
        bertaStackLabel: '{name} — {count} dominos',
        userTilesLabel: 'TES DOMINOS — {count} dominos',
        userTilesYourTurn: ' • à toi de jouer',
        placeOnLeft: 'Poser le domino au bout gauche de la chaîne',
        placeOnRight: 'Poser le domino au bout droit de la chaîne',
        // #551
        emptyChainHint: 'Touche un domino dans ta main, puis ici pour commencer',
        endTitleUser:    'Quelle belle partie !',
        endSubtitleUser: 'Tu as joué à merveille.',
        endTitleBerta:    'Quelle belle partie !',
        endSubtitleBerta: 'C\'était un plaisir de jouer avec toi.',
        endTitleTie:    'Une partie parfaitement équilibrée !',
        endSubtitleTie: 'On a bien joué tous les deux.',
      },
      solitairegame: {
        done: 'Terminé',
        doneAria: 'Terminer la réussite',
        undo: 'Annuler',
        undoAria: 'Annuler le dernier coup',
        stockAria: 'Piocher une carte dans le talon',
        recycleHint: 'Touche pour retourner la pioche',
        emptyColumnHint: 'Seul un Roi peut ouvrir une colonne vide',
        foundationAria: 'Pile, {suit}',
        stuckTitle: 'Les cartes ne sont pas de notre côté',
        stuckBody: 'Plus aucun coup possible cette fois — c\'est juste la chance des cartes. On essaie une nouvelle donne ? Tu n\'as qu\'à le dire, ou touche Terminé.',
        winTitle: 'Quelle belle partie !',
        winBody: 'Chaque carte est à sa place. Magnifiquement joué.',
        instructions: 'Touche une carte, puis l\'endroit où elle doit aller. Touche le talon pour d\'autres cartes.',
      },
      gamecarousel: {
        header: 'À quoi joue-t-on ?',
        play: 'Jouer',
        position: 'Jeu {index} sur {total}',
        favourite: 'Tu aimes ça',
        closeAria: 'Fermer le choix des jeux',
        prevAria: 'Jeu précédent',
        nextAria: 'Jeu suivant',
        titles: { solitaire: 'Réussite', domino: 'Dominos' },
        descriptions: {
          solitaire: 'Une paisible partie de réussite avec un jeu de cartes. Joue à ton rythme.',
          domino: 'Chacun son tour, en accolant les tuiles bout à bout. Détendu et convivial, sans score à courir.',
        },
      },
      idledim: {
        nightWakeHint: 'Appui long ou double tap pour réveiller',
      },
      settings: {
        title: 'Réglages',
        sectionCompanionStyle: 'Style du compagnon',
        styleSmiley: '😊 Smiley',
        styleWaveform: '📊 Onde',
        styleTextOnly: '🔤 Texte seul',
        gearAria: 'Réglages',
        refreshAria: 'Actualiser l’application (appui long)',
        // #551
        sectionMicrophone: 'Microphone',
      },
      idle: {
        tapToCall: 'Touche pour appeler',
        // #551
        callButton: '📞 Appeler {name}',
      },
      calling: {
        callingX: 'Appel de {name}…',
        // #551
        callEnded: 'Appel terminé',
      },
      incoming: {
        callingFrom: 'Appel entrant de {name}',
        accept: 'Répondre',
        decline: 'Refuser',
        // #551
        callingYou: '{name} t\'appelle',
        notNow: 'Pas maintenant',
      },
      hangup: {
        button: '📵 Raccrocher',
      },
      activity: {
        closeAria: 'Fermer',
      },
      micwarning: {
        cantHearWell: 'Je t\'entends mal — peux-tu parler un peu plus fort ?',
        // #544 — first-empty-turn reassurance, informal-tu register
        // matching the rest of the French tablet copy.
        takeYourTime: 'Pas de souci, prends ton temps',
        // #544 — 3+-empty-turn mic-hardware warning.
        checkMicrophone: 'Je t\'entends mal — vérifie ton microphone',
        // #551
        accessDenied: 'Accès au microphone nécessaire. Autorise-le dans les paramètres du navigateur.',
        notFound: 'Aucun microphone trouvé. Branche un microphone, s\'il te plaît.',
        generic: 'Erreur de microphone — réessaie',
      },
      mictest: {
        idle:      '🎤 Tester le microphone',
        recording: '⏹ Arrêter et vérifier',
        checking:  '⏳ Vérification…',
        weHeard:   '✅ Nous avons entendu : « {text} »',
        notHeard:  '❌ Nous ne t\'avons pas entendu clairement',
      },
      selftest: {
        section:     '🎧 Autotest audio',
        record:      '🔴 Enregistrer',
        recordAgain: '🔴 Réenregistrer',
        stop:        '⏹ Arrêter l\'enregistrement',
        play:        '▶️ Écouter',
        playing:     '🔊 Lecture en cours…',
        hint:        'Enregistre exactement ce qui est envoyé au serveur, puis le rejoue pour que vous puissiez juger la qualité captée.',
        error:       '❌ Échec de l\'enregistrement — la capture haute-fidélité (PCM) n\'est pas disponible dans ce navigateur.',
        raw:          '🎛 Micro brut (sans réduction de bruit / AGC)',
        rawHint:      'Désactivé = exactement ce que les appels envoient. Activé = micro brut, pour comparer le timbre / la chaleur.',
        tagRaw:       'brut',
        tagProcessed: 'tel quel',
      },
      domino: {
        moveError: 'Ce coup n\'a pas marché — essaie un autre domino.',
      },
      setup: {
        activatingTitle: 'Configuration de la tablette…',
        activatingBody:  'Un instant — nous lions cette tablette à ton compte.',
        failedTitle:     'Échec de la configuration',
        failedBody:      'Retourne au {strongOpen}portail famille{strongClose} et scanne un nouveau code QR.',
        notSetUpTitle:   'Cette tablette n\'est pas encore configurée',
        notSetUpBody:    'Demande à un membre de la famille d\'ouvrir le portail Amigo Mio et de scanner le code QR avec cette tablette.',
        confirmTitle:     'Configurer cette tablette ?',
        confirmBody:      'Cette tablette sera configurée pour {name}, avec {companion} comme compagne.',
        confirmBodyNoName:'Cette tablette sera configurée pour votre proche.',
        confirmButton:    'Configurer cette tablette',
        confirmCancel:    'Pas maintenant',
      },
    },
  },

  pt: {
    hero: {
      headline: 'Ela fala com a Lena todas as manhãs.',
      subline:
        'O Amigo Mio dá ao seu ente querido um companheiro que ouve de verdade — e que nunca esquece.',
      cta: 'Criar um perfil',
    },
    selfWedge: {
      headline: 'Está a configurar para si próprio?',
      subline:
        'Sim, também dá. Crie a sua própria conta; as perguntas adaptam-se.',
      cta: 'Criar a minha conta',
    },
    values: [
      {
        icon: '🎙️',
        heading: 'Fala sobre tudo',
        description:
          'Futebol, recordações, as notícias, um dia difícil. O Amigo Mio nunca julga e nunca muda de assunto.',
      },
      {
        icon: '🧠',
        heading: 'Lembra-se do que importa',
        description:
          'Cada nome, cada história, cada preferência. O seu ente querido nunca terá de se repetir.',
      },
      {
        icon: '👨‍👩‍👧',
        heading: 'Mantém a família próxima',
        description:
          'Vais ver como estão — sem nunca invadir os seus momentos privados.',
      },
    ],
    nonprofit: {
      badge: 'Sem fins lucrativos',
      text: 'O Amigo Mio é um projeto sem fins lucrativos. Mantemos os custos o mais baixos possível — e o primeiro mês é totalmente gratuito.',
      trial: '1 mês grátis · Sem cartão de crédito',
    },
    nav: {
      signIn: 'Entrar',
      createProfile: 'Criar um perfil',
    },
    footer: {
      alreadyHaveAccount: 'Já tens uma conta?',
      signIn: 'Entrar',
    },
    relations: {
      aunt: 'Tia',
      boyfriend: 'Namorado',
      brother: 'Irmão',
      caregiver: 'Cuidador',
      daughter: 'Filha',
      ex: 'Ex',
      father: 'Pai',
      girlfriend: 'Namorada',
      grandchild: 'Neto/a',
      mother: 'Mãe',
      nephew: 'Sobrinho',
      niece: 'Sobrinha',
      parent: 'Pai/mãe',
      partner: 'Parceiro/a',
      sibling: 'Irmão/irmã',
      sister: 'Irmã',
      son: 'Filho',
      spouse: 'Cônjuge',
      uncle: 'Tio',
      'family member': 'Outro familiar',
    },
    signup: {
      title: 'Criar um perfil',
      relationLabel: 'Sou o/a … da pessoa idosa',
      relationPlaceholder: '— selecionar —',
      change: '← Alterar',
      terms: { agree: 'Concordo com os', link: 'Termos da AmigoMio', read: '(ler)', hide: '(ocultar)', required: 'Aceite os Termos da AmigoMio para criar uma conta.' },
      name: 'Nome completo',
      email: 'Endereço de e-mail',
      password: 'Palavra-passe',
      confirmPassword: 'Confirmar palavra-passe',
      submit: 'Criar conta',
      haveAccount: 'Já tens uma conta?',
      signIn: 'Entrar',
      choice: {
        title: 'Para quem é?',
        subtitle: 'Escolha o que se adequa a si. Pode mudar de ideias no próximo ecrã.',
        forLoved: '👵  Para um ente querido',
        forSelf: '🧓  Para mim',
      },
      errors: {
        passwordMismatch: 'As palavras-passe não coincidem.',
        generic: 'Algo correu mal. Tenta novamente.',
        emailExists: 'Já existe uma conta com este e-mail.',
      },
    },
    login: {
      title: 'Entrar',
      email: 'Endereço de e-mail',
      password: 'Palavra-passe',
      submit: 'Entrar',
      noAccount: 'Ainda não tens conta?',
      signUp: 'Regista-te',
      errors: {
        invalid: 'E-mail ou palavra-passe inválidos.',
        generic: 'Algo correu mal. Tenta novamente.',
      },
    },
    intake: {
      title: 'Conhece a Mia',
      subtitle: 'Vou ajudar-te a criar um perfil para o teu ente querido. Demora cerca de 5 minutos — podes falar ou escrever.',
      talkWithMia: 'Falar com a Mia',
      chatWithMia: 'Conversar com a Mia',
      question: 'Pergunta',
      of: 'de',
      switchToVoice: 'Mudar para voz',
      switchToText: 'Mudar para texto',
      inputPlaceholder: 'Escreve a tua resposta…',
      tapToSpeak: 'Toca para falar',
      tapToSend: 'Toca para enviar',
      voiceConnecting: 'A ligar à Mia…',
      voiceReady: 'Fala — estou a ouvir.',
      voiceListening: 'A Mia está a falar…',
      voiceRecording: 'A ouvir…',
      voiceProcessing: 'A processar…',
      voiceDone: 'Tudo pronto! A levar-te ao teu painel…',
      voiceError: 'Erro de ligação. Atualiza a página, por favor.',
      voiceMicError: 'Acesso ao microfone negado.',
      errorStart: 'Algo correu mal. Atualiza e tenta novamente.',
      errorGeneric: 'Algo correu mal. Tenta novamente.',
      callPrompt: 'Quando estiveres pronto, carrega em Iniciar.',
      connecting: 'A ligar…',
      miaSpeaking: 'A Mia está a falar…',
      connectError: 'Não foi possível ligar. Atualiza a página e tenta novamente.',
      startBtn: 'Iniciar',
      startCall: 'Iniciar',
      endCall: 'Terminar chamada',
      startOver: 'Recomeçar',
    },
    portal: {
      welcome: 'Bem-vindo',
      logout: 'Sair',
      startIntake: 'Iniciar entrevista com a Mia',
      sections: {
        profile: { title: 'Perfil', description: 'Ver e editar o perfil do teu ente querido' },
        interests: { title: 'Interesses e Tópicos', description: 'Gerir interesses e tópicos bloqueados' },
        family: { title: 'Familiares', description: 'Adicionar e gerir ligações familiares' },
        friends: { title: 'Amigos', description: 'Ver e gerir amizades' },
        health: { title: 'Notas de saúde', description: 'Notas privadas de saúde e memória' },
        photos: { title: 'Fotos', description: 'Carregar fotos de família' },
        alerts: { title: 'Alertas', description: 'Alertas de proteção para o teu ente querido' },
        social: {
          title: 'Redes sociais', description: 'Facebook, YouTube, feeds RSS e atualizações da família',
          family_updates: 'Atualizações da família',
          family_updates_desc: 'Partilha algo que aconteceu na família — a Lena vai mencioná-lo na conversa.',
          person_name_placeholder: 'De quem se trata? (ex.: Carla)',
          relationship_placeholder: 'Relação (ex.: filha, neto)',
          what_happened_placeholder: 'O que aconteceu? Escreve uma atualização...',
          share_update: 'Partilhar atualização',
          youtube_channels: 'Canais do YouTube',
          youtube_desc: 'Adiciona um canal do YouTube — a Lena pode mencionar novos vídeos.',
          channel_name_placeholder: 'Nome do canal (ex.: canal de cozinha da Carla)',
          channel_url_placeholder: 'URL ou identificador do YouTube (ex.: @carla)',
          add_channel: 'Adicionar canal',
          rss_advanced: 'Avançado: feed RSS',
        },
        news: { title: 'Resumo de notícias', description: 'Notícias por interesse para conversas' },
        reminders: { title: 'Lembretes', description: 'Define lembretes e aniversários' },
        calls: { title: 'Chamadas proativas', description: 'Agenda chamadas de acompanhamento do companheiro' },
        companion: { title: 'Definições do companheiro', description: 'Configura como o companheiro aparece, soa e se comporta' },
        personality: {
          title: 'Personalidade',
          nurturer: 'Cuidador', straightTalker: 'Direto', joker: 'Brincalhão', intellectual: 'Intelectual', listener: 'Ouvinte',
          warmth: 'Carinho', humour: 'Humor', pace: 'Ritmo', formality: 'Formalidade', proactivity: 'Proatividade',
          warmthLeft: 'Caloroso e cuidador', warmthRight: 'Direto',
          humourLeft: 'Brincalhão e divertido', humourRight: 'Calmo e sério',
          paceLeft: 'Conversador e detalhado', paceRight: 'Conciso',
          formalityLeft: 'Informal e amigável', formalityRight: 'Formal e respeitoso',
          proactivityLeft: 'Toma a iniciativa', proactivityRight: 'Segue o utilizador',
          preview: 'Com estas definições, {name} será:',
          custom: 'Personalizado',
        },
        activity: { title: 'Atividade de conversa', description: 'Resumos recentes das conversas' },
        wellbeing: {
          title: 'Bem-estar',
          description: 'Resumo do bem-estar emocional a partir das conversas',
          intro: 'Foi assim que {name} se sentiu durante as conversas com {companion}',
          period: 'Período',
          mood: 'Estado de espírito',
          lastCall: 'Última chamada',
          thisWeek: 'Esta semana',
          lastWeek: 'Semana passada',
          lastMonth: 'Mês passado',
          noCalls: 'Sem chamadas',
          noData: 'Ainda não há dados de conversa disponíveis.',
          moods: { happy: 'Feliz', neutral: 'Neutro', low: 'Em baixo' },
          periodMoods: { happy: 'Maioritariamente positivo', neutral: 'Misto', low: 'Maioritariamente em baixo' },
          privacyNote: 'Baseado apenas no tom da conversa — não são partilhados detalhes',
        },
        gifts: {
          title: 'Ideias de presentes',
          description: 'Sugestões de presentes personalizadas',
          refresh: 'Atualizar sugestões',
          empty: 'Adiciona interesses para obter ideias de presentes',
        },
        pets: { title: 'Animais de estimação', description: 'Animais de estimação e as histórias à sua volta' },
        faith: { title: 'Fé', description: 'Fé, tradições e prática espiritual' },
        childhood: { title: 'Infância', description: 'Histórias e memórias dos primeiros anos' },
        work: { title: 'Vida profissional', description: 'Carreira e aquilo de que se orgulhavam' },
        account: { title: 'Conta', description: 'Definições da tua conta familiar' },
      },
      tablet: {
        close_photos: 'Fechar',
      },
      comingSoon: 'Em breve',
      detail: {
        backToDashboard: 'Voltar ao painel',
        loading: 'A carregar…',
        noIntakeTitle: 'Entrevista ainda não concluída',
        noIntakeText: 'Conclui a entrevista com a Mia para preencher esta secção.',
        fetchError: 'Não foi possível carregar os dados. Tenta novamente.',
        name: 'Nome',
        nickname: 'Alcunha',
        age: 'Idade',
        language: 'Idioma',
        location: 'Localização',
        noInterests: 'Ainda não há interesses registados.',
        noFamily: 'Ainda não há familiares registados.',
        noFriends: 'Ainda não foram adicionados amigos.',
        noHealth: 'Sem notas de saúde registadas.',
        familyOnly: 'Visível apenas para a família',
        addReminder: 'Adicionar lembrete',
        reminderPlaceholder: 'ex.: Ligar à Sophie, tomar comprimidos...',
        birthdayName: 'Nome',
        birthdayDate: 'Data (MM-DD)',
        addBirthday: 'Adicionar aniversário',
        pending: 'Pendente',
        completed: 'Concluído',
        noReminders: 'Ainda não há lembretes.',
        dueDate: 'Data de vencimento',
        optional: 'opcional',
      },
    },
    tablet: {
      callstrip: {
        status: {
          idle: '',
          wake_listening: 'A acordar',
          listening: 'A ouvir',
          transcribing: 'Deixa-me pensar',
          thinking: 'Deixa-me pensar',
          speaking: 'A falar',
          reconnecting: 'A reconectar',
          error: 'Algo correu mal',
          quota: 'A descansar um pouco',
          music_playing: 'Música a tocar',
          // #687 — wait mode label (PT).
          waiting: 'À espera',
        },
        endCall: 'Terminar chamada',
        hiccup: 'Pequeno soluço',
        tryingAgain: 'A tentar de novo',
        mutedMicLabel: 'microfone silenciado',
        // #551
        youSaidLabel: 'Disseste',
        hiccupTooltip: 'Soluço de ligação — o último turno não chegou limpo ao servidor.',
        // #700 — pause/resume button labels (PT).
        pauseLabel: 'Silêncio um momento',
        resumeLabel: 'Continuar',
      },
      yousaid: {
        placeholderTitle: 'Diz alguma coisa',
        placeholderBody: 'Estou aqui quando estiveres pronto.',
        liveLabel: 'Estou a ouvir-te…',
        frozenLabel: 'Disseste',
      },
      bertareply: {
        label: 'Lena',
        idlePlaceholder: 'Olá — estou aqui quando quiseres conversar.',
        you: 'Tu',  // #1032
      },
      photocarousel: {
        done: 'Pronto',
        prev: 'Foto anterior',
        next: 'Foto seguinte',
        zoomIn: 'Aproximar',
        zoomOut: 'Afastar',
        help: 'Ajuda',
        closeAria: 'Fechar fotos',
        bertaSaying: 'A Lena está a dizer',
      },
      videooverlay: {
        done: 'Pronto',
        play: 'Reproduzir',
        pause: 'Pausa',
        volumeDown: 'Baixar volume',
        volumeUp: 'Aumentar volume',
        closeAria: 'Fechar vídeo',
      },
      musicview: {
        nowPlaying: 'A tocar agora',
        fallbackTitle: 'Música',
        play: 'Reproduzir',
        pause: 'Pausa',
        volumeDown: 'Baixar volume',
        volumeUp: 'Aumentar volume',
        done: 'Pronto',
        closeAria: 'Fechar reprodutor de música',
      },
      dominogame: {
        done: 'Pronto',
        doneAria: 'Terminar o jogo de dominó',
        knock: 'Bater (saltar)',
        knockAria: 'Bater — saltar a vez (apenas quando não tens peça jogável)',
        thinking: '{name} está a pensar…',
        passed: '{name} passou',
        bertaStackLabel: '{name} — {count} peças',
        userTilesLabel: 'AS TUAS PEÇAS — {count} peças',
        userTilesYourTurn: ' • é a tua vez',
        placeOnLeft: 'Colocar peça no extremo esquerdo da linha',
        placeOnRight: 'Colocar peça no extremo direito da linha',
        // #551
        emptyChainHint: 'Toca numa peça da tua mão e depois aqui para começar',
        endTitleUser:    'Que jogo bonito!',
        endSubtitleUser: 'Jogaste lindamente.',
        endTitleBerta:    'Que jogo bonito!',
        endSubtitleBerta: 'Foi um prazer jogar contigo.',
        endTitleTie:    'Um jogo perfeitamente equilibrado!',
        endSubtitleTie: 'Os dois jogámos bem.',
      },
      solitairegame: {
        done: 'Pronto',
        doneAria: 'Terminar o jogo de paciência',
        undo: 'Desfazer',
        undoAria: 'Desfazer a última jogada',
        stockAria: 'Tirar uma carta do baralho',
        recycleHint: 'Toca para virar o monte',
        emptyColumnHint: 'Só um Rei pode começar uma coluna vazia',
        foundationAria: 'Base, {suit}',
        stuckTitle: 'As cartas não estão a ajudar',
        stuckBody: 'Não há mais jogadas nesta mão — é só a sorte das cartas. Queres tentar uma mão nova? É só pedires, ou toca em Pronto.',
        winTitle: 'Que jogo bonito!',
        winBody: 'Todas as cartas no lugar. Jogaste lindamente.',
        instructions: 'Toca numa carta e depois onde ela deve ir. Toca no baralho para mais cartas.',
      },
      gamecarousel: {
        header: 'A que vamos jogar?',
        play: 'Jogar',
        position: 'Jogo {index} de {total}',
        favourite: 'Gostas deste',
        closeAria: 'Fechar a seleção de jogos',
        prevAria: 'Jogo anterior',
        nextAria: 'Jogo seguinte',
        titles: { solitaire: 'Paciência', domino: 'Dominó' },
        descriptions: {
          solitaire: 'Um sossegado jogo de paciência com um baralho de cartas. Joga ao teu ritmo.',
          domino: 'À vez, encaixando as peças de ponta a ponta. Tranquilo e agradável, sem pontuação a perseguir.',
        },
      },
      idledim: {
        nightWakeHint: 'Mantém premido ou toca duas vezes para acordar',
      },
      settings: {
        title: 'Definições',
        sectionCompanionStyle: 'Estilo do companheiro',
        styleSmiley: '😊 Sorridente',
        styleWaveform: '📊 Onda',
        styleTextOnly: '🔤 Apenas texto',
        gearAria: 'Definições',
        refreshAria: 'Atualizar a aplicação (premir longamente)',
        // #551
        sectionMicrophone: 'Microfone',
      },
      idle: {
        tapToCall: 'Toca para ligar',
        // #551
        callButton: '📞 Ligar a {name}',
      },
      calling: {
        callingX: 'A ligar para {name}…',
        // #551
        callEnded: 'Chamada terminada',
      },
      incoming: {
        callingFrom: 'Chamada a entrar de {name}',
        accept: 'Atender',
        decline: 'Recusar',
        // #551
        callingYou: '{name} está a ligar-te',
        notNow: 'Agora não',
      },
      hangup: {
        button: '📵 Desligar',
      },
      activity: {
        closeAria: 'Fechar',
      },
      micwarning: {
        cantHearWell: 'Não te ouço muito bem — podes falar um pouco mais alto?',
        // #544 — first-empty-turn reassurance, informal-tu register
        // (pt-PT). Native-speaker review can refine for pt-BR.
        takeYourTime: 'Não te preocupes, tem calma',
        // #544 — 3+-empty-turn mic-hardware warning.
        checkMicrophone: 'Estou com dificuldade em ouvir-te — verifica o microfone',
        // #551
        accessDenied: 'É necessário acesso ao microfone. Permite isso nas definições do navegador.',
        notFound: 'Não foi encontrado nenhum microfone. Liga um microfone, por favor.',
        generic: 'Erro de microfone — tenta novamente',
      },
      mictest: {
        idle:      '🎤 Testar microfone',
        recording: '⏹ Parar e verificar',
        checking:  '⏳ A verificar…',
        weHeard:   '✅ Ouvimos: "{text}"',
        notHeard:  '❌ Não te ouvimos com clareza',
      },
      selftest: {
        section:     '🎧 Autoteste de áudio',
        record:      '🔴 Gravar',
        recordAgain: '🔴 Gravar novamente',
        stop:        '⏹ Parar gravação',
        play:        '▶️ Reproduzir',
        playing:     '🔊 A reproduzir…',
        hint:        'Grava exatamente o que é enviado para o servidor e depois reproduz para que possa avaliar a qualidade captada.',
        error:       '❌ Falha na gravação — a captação de alta fidelidade (PCM) não está disponível neste navegador.',
        raw:          '🎛 Microfone em bruto (sem supressão de ruído / AGC)',
        rawHint:      'Desligado = exatamente o que as chamadas enviam. Ligado = microfone em bruto, para comparar o tom / a riqueza.',
        tagRaw:       'em bruto',
        tagProcessed: 'como enviado',
      },
      domino: {
        moveError: 'Essa jogada não resultou — experimenta outra peça.',
      },
      setup: {
        activatingTitle: 'A configurar a tablet…',
        activatingBody:  'Um momento — estamos a ligar esta tablet à tua conta.',
        failedTitle:     'A configuração falhou',
        failedBody:      'Volta ao {strongOpen}portal família{strongClose} e digitaliza um novo código QR.',
        notSetUpTitle:   'Esta tablet ainda não está configurada',
        notSetUpBody:    'Pede a um familiar para abrir o portal Amigo Mio e digitalizar o código QR com esta tablet.',
        confirmTitle:     'Configurar este tablet?',
        confirmBody:      'Este tablet será configurado para {name}, com {companion} como companheira.',
        confirmBodyNoName:'Este tablet será configurado para o seu ente querido.',
        confirmButton:    'Configurar este tablet',
        confirmCancel:    'Agora não',
      },
    },
  },
};

const SUPPORTED = ['en', 'nl', 'es', 'it', 'de', 'fr', 'pt'];

// #1107 follow-on — the large /intake/review form strings live in their
// own module; inject them as the `intakeReview` namespace per language so
// t.intakeReview.* works with the same English deep-merge fallback as
// every other namespace. Missing-language keys fall back to English.
for (const _lang of SUPPORTED) {
  if (translations[_lang]) {
    translations[_lang].intakeReview = INTAKE_REVIEW_STRINGS[_lang] || INTAKE_REVIEW_STRINGS.en;
  }
}

// #1107 — native-name labels for the language switcher, covering ALL of
// SUPPORTED. This used to be a hand-maintained 5-entry map in Landing.js
// that drifted (fr + pt were missing, so those buttons rendered blank).
// Single source here; Landing + the auth-page LanguageSwitcher import it.
const LANG_LABELS = {
  en: 'English',
  nl: 'Nederlands',
  de: 'Deutsch',
  fr: 'Français',
  es: 'Español',
  it: 'Italiano',
  pt: 'Português',
};

// #1107 — the chosen language must follow the user across a direct load
// of /signup or a refresh, not just in-app navigation. We persist the
// pick under this key and read it back first in detectLanguage().
const LANG_STORAGE_KEY = 'am_lang';

function _readStoredLanguage() {
  try {
    const v = window.localStorage.getItem(LANG_STORAGE_KEY);
    return v && SUPPORTED.includes(v) ? v : null;
  } catch {
    return null;  // localStorage blocked (private mode / SSR) — fall through
  }
}

export function persistLanguage(lang) {
  try {
    if (SUPPORTED.includes(lang)) {
      window.localStorage.setItem(LANG_STORAGE_KEY, lang);
    }
  } catch {
    /* storage unavailable — the choice still applies in-memory this session */
  }
}

export function detectLanguage() {
  // Priority: explicit prior choice (persisted) → browser locale → en.
  const stored = _readStoredLanguage();
  if (stored) return stored;
  const raw = (typeof navigator !== 'undefined'
    && (navigator.language || navigator.userLanguage)) || 'en';
  const lang = raw.split('-')[0].toLowerCase();
  return SUPPORTED.includes(lang) ? lang : 'en';
}

// Deep-merge two objects key-by-key, preferring `primary` over `fallback`.
// Used by getTranslations so a non-en language with a missing namespace
// (today: `tablet` only exists in `en`) falls back to English per-key
// instead of crashing with "cannot read property of undefined". Keeps
// translation rollouts incremental — Phase 1 of #441 only ships the
// English tablet keys; Phase 3 fills the others without needing to
// stub English values into all five languages first.
function _deepMerge(primary, fallback) {
  const out = { ...fallback };
  for (const key of Object.keys(primary || {})) {
    const p = primary[key];
    const f = fallback ? fallback[key] : undefined;
    if (p && typeof p === 'object' && !Array.isArray(p)
        && f && typeof f === 'object' && !Array.isArray(f)) {
      out[key] = _deepMerge(p, f);
    } else {
      out[key] = p;
    }
  }
  return out;
}

export function getTranslations(lang) {
  const primary = translations[lang] || translations['en'];
  if (lang === 'en') return primary;
  return _deepMerge(primary, translations['en']);
}

export { SUPPORTED, LANG_LABELS, LANG_STORAGE_KEY };
export default translations;
